import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  PolicyScenario,
  PolicyImpactResults,
  calculatePolicyImpact,
  getCachedPolicyImpactResults,
  getDefaultScenario,
  getPredefinedScenarios,
  type PolicyLoadProgress,
} from '../../services/policyImpactService';
import { getMicromobilityStats, MicromobilityStats } from '../../services/policyImpactService';
import {
  InterventionAreas,
  getDefaultInterventionAreas,
  getDefaultEnergyEfficiencyComponents,
  normalizeEnergyEfficiencyComponents,
} from '../../types/policyImpact';
import {
  computeTotalEfficiencyPercentFromComponents,
  solveOtherEfficiencyPercentForCombinedTarget,
  applyEfficiencyLayer,
  applyEfficiencyForYearWithContributions,
  COMMERCIAL_ANNUAL_GROWTH_RATE,
  type EfficiencyLayerOptions,
  type EfficiencyContributions,
} from '../../services/buildingsEfficiencyModel';
import {
  getPathwayTherms,
  getPathwayKwh,
  bauExistingStockEfficiencyPercentBy2045,
  RNG_SHARE_2045_BY_SCENARIO,
  getFlatBuildingsPathway,
} from '../../services/buildingsBauPathways';
import { getNaturalGasConsumptionBySector, getElectricityConsumptionBySector } from '../../services/buildingsEnergyDataService';
import { getCachedBaseline, buildingsBaselineCacheKey } from '../../services/policyBaselineCache';
import type { EnergyEfficiencySubPolicyValues } from './InterventionAreasControl';
import {
  EnergyEfficiencyOverTimeChart,
  EfficiencyByPolicyChart,
  type EnergyEfficiencyOverTimeData,
} from './EnergyEfficiencyCharts';
import PolicyImpactChart from './PolicyImpactChart';
import ParkingPricingPolicy from './ParkingPricingPolicy';
import VehicleElectrificationPolicy from './VehicleElectrificationPolicy';
import BuildingElectrificationPolicy from './BuildingElectrificationPolicy';
import InterventionAreasControl from './InterventionAreasControl';
import TransitServiceExpansionCharts from './TransitServiceExpansionCharts';
import CompactDevelopmentCharts from './CompactDevelopmentCharts';
import {
  fetchCompactDevelopmentSeries,
  fetchHousingUnitsByYear,
  CompactDevelopmentSeries,
  GROWTH_RATE_PERCENT_MAX,
  COMPACT_DEVELOPMENT_MIN_DENSITY,
  ldvReductionMtFromNetStatewideAnnual,
} from '../../services/compactDevelopmentService';
import type { TimeSeriesEmissionData } from '../../services/policyTimeSeriesService';
import {
  generatePolicyTimeSeriesData,
  generateFlatBaselineTimeSeries,
  getBuildingEnergyTimeSeries,
} from '../../services/policyTimeSeriesService';
import type { BuildingEnergyComparisonSeries } from './buildingElectrificationChartTypes';
import { computeBuildingsCostResults, type BuildingBillImpactDetail } from '../../services/buildingsCostModel';
import { computeTransportCostResults, ICE_MPG, EV_KWH_PER_MILE } from '../../services/transportCostModel';
import { ELECTRICITY_PRICE_PER_KWH } from '../../services/buildingsCostModel';
import {
  DEFAULT_COST_ASSUMPTIONS,
  costAssumptionsToBuildingsOverrides,
  type CostAssumptions,
} from '../../services/costAssumptions';
import { fetchLatestElectricityPricesForLocation } from '../../services/electricityPricesByGeographyService';
import { fetchLatestTotalEstablishmentsForGeofips } from '../../services/businessEstablishmentsService';
import { getDefaultBuildingEndUseAssumptions } from '../../services/buildingEndUseAssumptions';
import {
  fetchCommercialGasEndUseResolved,
  commercialSharesNearDefaults,
} from '../../services/commercialGasEndUseService';
import {
  fetchCaliforniaCapGhgTargetForGeofips,
  parseCapAdoptionDisplayYear,
} from '../../services/californiaCapGhgTargetsService';
import { ScenarioCostAnalysisSection } from './ScenarioCostAnalysisSection';
import { PortfolioCostSection } from './PortfolioCostSection';
import { PolicyToolCostAssumptionsSection } from './PolicyToolCostAssumptionsSection';
import { currencyShort } from './portfolioCostUtils';
import { supabaseClient } from '../../services/supabase';
import { loadScenarioById, saveScenario } from '../../services/scenarioService';
import { useAuth } from '../../contexts/AuthContext';
import {
  parseScenarioFromUrl,
  validateScenarioUrlParams,
  applyScenarioPresetAndOverrides,
  updateScenarioUrl,
  buildScenarioStateFromTool,
  serializeScenarioState,
  hydrateToolFromScenarioState,
  type LocalPresetParam,
} from '../../utils/scenarioUrl';
import {
  absoluteEvToBeyondBauSlider,
  beyondBauEvSliderToAbsolute,
} from '../../utils/evBeyondBauSlider';
import type { ScenarioReportInput } from '../../utils/scenarioReportGenerator';
import PolicyPanel from './PolicyPanel';
import { useClimatePlansAdmin } from '../../hooks/useClimatePlansAccess';
import { LocationSelector } from '../LocationSelector';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import './PolicyImpactTool.css';

/** Building-only emissions by year (MT CO₂e), for Building Electrification panel charts */
export interface BuildingEmissionsTimeSeries {
  baseline: Array<{ year: number; naturalGas: number; electricity: number }>;
  policy: Array<{ year: number; naturalGas: number; electricity: number }>;
}

const CHART_YEAR_MIN = 2023;
const CHART_YEAR_MAX = 2045;

/** Appended to CAP default-target info bubble (California Polytechnic CAP database). */
const CALIFORNIA_CAP_DATABASE_CITATION =
  'Boswell, M.R. and Greve, A.I. (2025). California climate action plan database (Version: 1 November 2025) [Data set]. California Polytechnic State University. https://climate.calpoly.edu/CCAP';

function capTargetDefaultsBubbleBodyWithCitation(body: string): string {
  return `${body}\n\n${CALIFORNIA_CAP_DATABASE_CITATION}`;
}

function deriveBuildingEmissionsTimeSeries(
  baselineData: TimeSeriesEmissionData[],
  projectedData: TimeSeriesEmissionData[]
): BuildingEmissionsTimeSeries {
  const filter = (rows: TimeSeriesEmissionData[]) =>
    rows
      .filter((d) => d.year >= CHART_YEAR_MIN && d.year <= CHART_YEAR_MAX)
      .map((d) => ({
        year: d.year,
        naturalGas: d.naturalGasEmissions ?? 0,
        electricity: d.electricityEmissions ?? 0,
      }));
  return {
    baseline: filter(baselineData),
    policy: filter(projectedData),
  };
}

interface PolicyImpactToolProps {
  location: string;
  geofips?: number | null;
}

const TRANSIT_ELASTICITY = 0.7; // Handy et al. 2013
const TRANSIT_MODE_SHARE_PERCENT = 20; // Placeholder until wired to Census
const TRANSIT_STATEWIDE_MODE_SHIFT_PERCENT = 57.8; // FHWA 2017
const TRANSIT_IMPLEMENTATION_YEAR = 2025;
const TRANSIT_PROJECTION_END_YEAR = 2045;

/** Stacked-chart target trajectory: reference inventory year vs linear path end year. */
const TARGET_REF_YEAR_MIN = 2005;
const TARGET_REF_YEAR_MAX = 2023;
const TARGET_END_YEAR_MIN = 2025;
const TARGET_END_YEAR_MAX = 2045;

function clampInt(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.max(lo, Math.min(hi, Math.round(n)));
}

const calculateTransitVmtReductionPercent = (serviceIncreasePercent: number): number => {
  const vmtReductionPct =
    TRANSIT_ELASTICITY *
    (serviceIncreasePercent / 100) *
    (TRANSIT_MODE_SHARE_PERCENT / 100) *
    (TRANSIT_STATEWIDE_MODE_SHIFT_PERCENT / 100) *
    100;
  return Math.max(0, Number(vmtReductionPct.toFixed(3)));
};

const PolicyImpactTool: React.FC<PolicyImpactToolProps> = ({ location, geofips = null }) => {
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.count('PolicyImpactTool:render');
  }
  const { user } = useAuth();
  const resultsRef = React.useRef<PolicyImpactResults | null>(null);
  const [scenario, setScenario] = useState<PolicyScenario>(getDefaultScenario());
  const [results, setResults] = useState<PolicyImpactResults | null>(null);
  resultsRef.current = results;
  const [loading, setLoading] = useState<boolean>(false);
  const [loadProgress, setLoadProgress] = useState<PolicyLoadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [interventionAreas, setInterventionAreas] = useState<InterventionAreas>(getDefaultInterventionAreas());
  const [activeChart, setActiveChart] = useState<'line' | 'contributions' | 'forecast' | 'stacked'>('stacked');
  const [targetReductionPercent, setTargetReductionPercent] = useState<number>(80);
  const [targetBaselineYear, setTargetBaselineYear] = useState<number>(2015);
  const [targetEndYear, setTargetEndYear] = useState<number>(2045);
  /** Explains when GHG target line defaults come from california_cap_ghg_targets. */
  const [capTargetDefaultsBanner, setCapTargetDefaultsBanner] = useState<string | null>(null);
  /** Tooltip on reference-year input when CAP baseline was before 2005 and we use 2005 instead. */
  const [capReferenceYearSubstitutionHint, setCapReferenceYearSubstitutionHint] = useState<string | undefined>(undefined);
  const capTargetDefaultsInfoId = useId();
  const [stackedTotals, setStackedTotals] = useState<null | {
    baselineYear: number;
    baselineTotal: number;
    projectedYear: number;
    projectedWithPoliciesTotal: number;
    totalReduction: number;
    percentReduction: number;
  }>(null);
  const [expandedIntervention, setExpandedIntervention] = useState<keyof InterventionAreas | null>(null);
  const [expandedSubPolicy, setExpandedSubPolicy] = useState<string | null>(null);
  const { isAdmin: canExpandDetails } = useClimatePlansAdmin();

  useEffect(() => {
    if (canExpandDetails) return;
    setExpandedIntervention(null);
    setExpandedSubPolicy(null);
  }, [canExpandDetails]);
  const [subPolicyValues, setSubPolicyValues] = useState({
    transitOrientedDevelopment: 0,
    downtownHomesPercent: 70,
    activeTransportation: 0,
    transitImprovements: 0,
    parkingPricing: 0,
    micromobility: 0,
    otherInitiatives: 0,
  });
  const [micromobilityStats, setMicromobilityStats] = useState<MicromobilityStats | null>(null);
  const [micromobilityVMTReductionPercent, setMicromobilityVMTReductionPercent] = useState<number>(0);
  const [transitRevenueMiles, setTransitRevenueMiles] = useState<{ year: number; miles: number } | null>(null);
  const [transitServiceHistory, setTransitServiceHistory] = useState<Array<{ year: number; miles: number }>>([]);
  const [transitVMTReductionPercent, setTransitVMTReductionPercent] = useState<number>(0);
  const [emfacVersion, setEmfacVersion] = useState<'2021' | '2025'>('2021');
  const [buildingsBauScenario, setBuildingsBauScenario] = useState<'conservative' | 'current_policy' | 'accelerated'>('current_policy');
  const [localScenarioName, setLocalScenarioName] = useState<LocalPresetParam | undefined>(undefined);
  const [scenarioId, setScenarioId] = useState<string | undefined>(undefined);
  const [savingScenario, setSavingScenario] = useState<boolean>(false);
  /** Default: incremental vs BAU so portfolio cash flow matches emissions baseline vs scenario (transport $≈0 when only buildings change). */
  const [portfolioCostView, setPortfolioCostView] = useState<'incremental_vs_bau' | 'bau_costs' | 'total_costs'>('incremental_vs_bau');
  const [buildingsRngPercent, setBuildingsRngPercent] = useState<number>(() => RNG_SHARE_2045_BY_SCENARIO.current_policy * 100);
  const [buildingsCop, setBuildingsCop] = useState<number>(3.5); // Heat pump COP 2.5–4; 3.5 = efficient unit in mild climate
  const [buildingEmissionsTimeSeries, setBuildingEmissionsTimeSeries] = useState<BuildingEmissionsTimeSeries | null>(null);
  /** Full time series for Buildings Cost layer (baseline + projected emissions). */
  const [timeSeriesDataForCost, setTimeSeriesDataForCost] = useState<{
    baselineData: TimeSeriesEmissionData[];
    projectedData: TimeSeriesEmissionData[];
  } | null>(null);
  const [compactDevelopmentSeries, setCompactDevelopmentSeries] = useState<CompactDevelopmentSeries | null>(null);
  const [efficiencyChartData, setEfficiencyChartData] = useState<EnergyEfficiencyOverTimeData | null>(null);
  const [efficiencyContributions2045, setEfficiencyContributions2045] = useState<EfficiencyContributions | null>(null);
  const [efficiencySegmentShares, setEfficiencySegmentShares] = useState<{
    resExisting2030: number;
    resExisting2045: number;
    comExisting2030: number;
    comExisting2045: number;
  } | null>(null);
  /** Housing units by year for building electrification CAPEX ($/unit). Fetched when geofips available. */
  const [housingUnitsByYear, setHousingUnitsByYear] = useState<Record<number, number> | null>(null);
  /** business_establishments total row — scales commercial intensity in buildings cost model. */
  const [commercialEstablishmentCount, setCommercialEstablishmentCount] = useState<number | null>(null);
  const [electricityPriceContext, setElectricityPriceContext] = useState<{
    pricePerKwh: number;
    source: string;
  }>({
    pricePerKwh: ELECTRICITY_PRICE_PER_KWH,
    source: 'Default building cost assumption',
  });
  /** Drives portfolio buildings/transport cost, decision summary, and scenario cost comparison. */
  const [policyCostAssumptions, setPolicyCostAssumptions] = useState<CostAssumptions>(DEFAULT_COST_ASSUMPTIONS);
  const [policyCostIceMpg, setPolicyCostIceMpg] = useState(ICE_MPG);
  const [policyCostEvKwhPerMile, setPolicyCostEvKwhPerMile] = useState(EV_KWH_PER_MILE);
  const urlInitializedRef = React.useRef(false);
  /** True if URL contained `compactDevelopment=` (any value, including 0) — skip auto BAU housing-growth default. */
  const urlHadCompactDevelopmentParamRef = React.useRef(false);
  /** True after a saved scenario was loaded from `scenarioId` in URL — skip auto BAU default. */
  const savedScenarioLoadedFromUrlRef = React.useRef(false);
  /** Legacy URL `ev` without `evMode=beyond` — absolute 2045 fleet EV%, convert once BAU is known. */
  const pendingLegacyAbsoluteEvRef = React.useRef<number | null>(null);
  /** Saved scenario stored absolute EV in interventionAreas (pre–beyond-BAU). */
  const pendingSavedAbsoluteEvRef = React.useRef<number | null>(null);
  const legacyEvMigratedRef = React.useRef(false);
  /** geofips for which we already applied location BAU % to the Compact Development slider (fresh sessions). */
  const prevCompactFetchGeofipsRef = React.useRef<number | null>(null);
  /** Last `impactHeavyDepsKey` for which `calculatePolicyImpact` completed; scenario-only edits skip blocking loading if this matches. */
  const lastCompletedImpactHeavyKeyRef = React.useRef<string>('');
  /** True when URL or saved scenario explicitly set compact housing growth to 0 (allow slider below BAU floor). */
  const [compactAllowBelowBauGrowth, setCompactAllowBelowBauGrowth] = useState(false);
  /** False until first-pass URL / saved-scenario hydration finishes (avoids predefined scenario overwriting geography commercial shares). */
  const [urlScenarioHydrated, setUrlScenarioHydrated] = useState(false);

  useEffect(() => {
    setPolicyCostAssumptions((a) =>
      a.electricityPricePerKwh === electricityPriceContext.pricePerKwh
        ? a
        : { ...a, electricityPricePerKwh: electricityPriceContext.pricePerKwh },
    );
  }, [electricityPriceContext.pricePerKwh]);

  const resetPolicyCostAssumptions = useCallback(() => {
    setPolicyCostAssumptions({
      ...DEFAULT_COST_ASSUMPTIONS,
      electricityPricePerKwh: electricityPriceContext.pricePerKwh,
    });
    setPolicyCostIceMpg(ICE_MPG);
    setPolicyCostEvKwhPerMile(EV_KWH_PER_MILE);
  }, [electricityPriceContext.pricePerKwh]);

  const policyBuildingsCostAssumptions = useMemo(
    () => costAssumptionsToBuildingsOverrides(policyCostAssumptions),
    [policyCostAssumptions],
  );

  const policyTransportCostOptionsBase = useMemo(
    () => ({
      gasolinePricePerGallon: policyCostAssumptions.gasolinePricePerGallon,
      electricityPricePerKwh: policyCostAssumptions.electricityPricePerKwh,
      discountRate: policyCostAssumptions.discountRate,
      iceMpg: policyCostIceMpg,
      evKwhPerMile: policyCostEvKwhPerMile,
      evIncrementalCost: policyCostAssumptions.evIncrementalCost,
      chargerCostPerEv: policyCostAssumptions.chargerCostPerEv,
      emfacVersion,
    }),
    [policyCostAssumptions, policyCostIceMpg, policyCostEvKwhPerMile, emfacVersion],
  );

  /** Annual housing trajectory for efficiency stock + building electrification $/unit: U(y) = U₀(1+g)^(y−2023), g = Compact Development %/100. */
  const housingUnitsByYearForPolicyPath = useMemo(() => {
    const g =
      Math.min(GROWTH_RATE_PERCENT_MAX, Math.max(0, subPolicyValues.transitOrientedDevelopment ?? 0)) / 100;
    const rows = compactDevelopmentSeries?.rows ?? [];
    const r2023 = rows.find((r) => r.year === 2023);
    const U0FromCompact =
      r2023?.housingUnitsBau && r2023.housingUnitsBau > 0 ? r2023.housingUnitsBau : 0;
    const U0FromFetch =
      housingUnitsByYear?.[2023] && housingUnitsByYear[2023]! > 0 ? housingUnitsByYear[2023]! : 0;
    const U0 = U0FromCompact > 0 ? U0FromCompact : U0FromFetch;
    if (U0 <= 0) return housingUnitsByYear;
    const out: Record<number, number> = {};
    for (let y = 2023; y <= 2045; y++) {
      out[y] = y <= 2023 ? U0 : U0 * Math.pow(1 + g, y - 2023);
    }
    return out;
  }, [subPolicyValues.transitOrientedDevelopment, compactDevelopmentSeries, housingUnitsByYear]);

  /** Housing / stock inputs for efficiency layer (compact dev + baseline pathway + establishments). */
  const efficiencyLayerOptions: EfficiencyLayerOptions | undefined = useMemo(() => {
    const rows = compactDevelopmentSeries?.rows ?? [];
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    const pathway = baseline?.buildingsBauPathway;
    const bld = baseline?.buildingsEnergyData;

    const g =
      Math.min(GROWTH_RATE_PERCENT_MAX, Math.max(0, subPolicyValues.transitOrientedDevelopment ?? 0)) / 100;
    const r2023Row = rows.find((r) => r.year === 2023);
    const U0FromCompact =
      r2023Row?.housingUnitsBau && r2023Row.housingUnitsBau > 0 ? r2023Row.housingUnitsBau : 0;
    const U0FromFetch =
      housingUnitsByYear?.[2023] && housingUnitsByYear[2023]! > 0 ? housingUnitsByYear[2023]! : 0;
    const U0 = U0FromCompact > 0 ? U0FromCompact : U0FromFetch;

    const housingUnitsByYearPolicy = (year: number) => {
      if (U0 <= 0) return 0;
      if (year <= 2023) return U0;
      return U0 * Math.pow(1 + g, year - 2023);
    };

    let existingResShareByYear: NonNullable<EfficiencyLayerOptions['existingResShareByYear']> | undefined;
    if (U0 > 0) {
      existingResShareByYear = (year: number) => {
        if (year <= 2023) return 1;
        const uy = housingUnitsByYearPolicy(year);
        if (uy <= 0) return 1;
        return Math.max(0, Math.min(1, U0 / uy));
      };
    }

    let stockModel: EfficiencyLayerOptions['stockModel'];
    if (U0 > 0 && pathway && bld && commercialEstablishmentCount != null && commercialEstablishmentCount > 0) {
        const totalT23 = getPathwayTherms(pathway, 2023);
        const totalK23 = getPathwayKwh(pathway, 2023);
        const { resTherms: rawRes, comTherms: rawCom } = getNaturalGasConsumptionBySector(bld, 2023);
        const gasSum = rawRes + rawCom;
        const resFracT = gasSum > 0 ? rawRes / gasSum : 0.6;
        const { resKwh, comKwh } = getElectricityConsumptionBySector(bld, 2023);
        const kSum = resKwh + comKwh;
        const resFracK = kSum > 0 ? resKwh / kSum : resFracT;
        const E0 = commercialEstablishmentCount;
        const establishmentsByYear = (year: number) => {
          if (year <= 2023) return E0;
          return E0 * Math.pow(1 + COMMERCIAL_ANNUAL_GROWTH_RATE, year - 2023);
        };
        stockModel = {
          housingUnits2023: U0,
          housingUnitsByYear: housingUnitsByYearPolicy,
          resTherms2023: totalT23 * resFracT,
          resKwh2023: totalK23 * resFracK,
          comTherms2023: totalT23 * (1 - resFracT),
          comKwh2023: totalK23 * (1 - resFracK),
          establishments2023: E0,
          establishmentsByYear,
          bauExistingEfficiencyPercent2045:
            scenario.bauExistingStockEfficiencyPercent2045 != null &&
            Number.isFinite(scenario.bauExistingStockEfficiencyPercent2045)
              ? Math.max(0, Math.min(40, Number(scenario.bauExistingStockEfficiencyPercent2045)))
              : bauExistingStockEfficiencyPercentBy2045(buildingsBauScenario),
          applyBauExistingEfficiency: scenario.applyBauExistingStockEfficiency !== false,
        };
    }

    if (!existingResShareByYear && !stockModel) return undefined;
    const out: EfficiencyLayerOptions = {};
    if (existingResShareByYear) out.existingResShareByYear = existingResShareByYear;
    if (stockModel) out.stockModel = stockModel;
    return out;
  }, [
    subPolicyValues.transitOrientedDevelopment,
    compactDevelopmentSeries,
    housingUnitsByYear,
    location,
    emfacVersion,
    buildingsBauScenario,
    commercialEstablishmentCount,
    scenario.applyBauExistingStockEfficiency,
    scenario.bauExistingStockEfficiencyPercent2045,
  ]);

  /** Compact Development slider minimum = BAU housing growth (%/yr), like EV slider min = current EV share. */
  const compactDevelopmentSliderMinPercent = useMemo(() => {
    if (compactAllowBelowBauGrowth) return 0;
    const b = compactDevelopmentSeries?.bauGrowthPercent;
    if (b != null && Number.isFinite(b) && b >= 0) {
      return Math.round(Math.min(GROWTH_RATE_PERCENT_MAX, b) * 10) / 10;
    }
    return 0;
  }, [compactAllowBelowBauGrowth, compactDevelopmentSeries?.bauGrowthPercent]);

  /** Positive MT CO₂e LDV reduction in 2045 from compact net statewide series (net is negative when saving). */
  const compactLdvReductionMt2045 = useMemo(() => {
    const policyYear = 2045;
    const signed =
      compactDevelopmentSeries?.rows?.find((r) => r.year === policyYear)?.netStatewideAnnual ??
      compactDevelopmentSeries?.rows?.slice(-1)[0]?.netStatewideAnnual;
    return ldvReductionMtFromNetStatewideAnnual(signed);
  }, [compactDevelopmentSeries]);

  /** Incremental compact policy VMT reduction (% pts) vs baseline LDV, rolled into headline VMT slider. */
  const compactVmtReductionPercent = useMemo(() => {
    const mt = compactLdvReductionMt2045;
    const ldg = results?.baseline?.lightDutyGHG ?? 0;
    if (!(mt > 0) || !(ldg > 0)) return 0;
    return Math.min(20, Math.max(0, (mt / ldg) * 100));
  }, [compactLdvReductionMt2045, results?.baseline?.lightDutyGHG]);

  /** Stable reference for ScenarioCostAnalysisSection — inline object was new every render and blew runComparison/debounce deps. */
  const scenarioCostAnalysisOptions = useMemo(
    () => ({
      buildingsBauScenario,
      buildingsRngPercent,
      buildingsCop,
      emfacVersion,
      efficiencyLayerOptionsByLocation: efficiencyLayerOptions ? { [location]: efficiencyLayerOptions } : undefined,
    }),
    [buildingsBauScenario, buildingsRngPercent, buildingsCop, emfacVersion, location, efficiencyLayerOptions],
  );

  const handleTimeSeriesDataChange = React.useCallback((baselineData: TimeSeriesEmissionData[], projectedData: TimeSeriesEmissionData[]) => {
    setBuildingEmissionsTimeSeries(deriveBuildingEmissionsTimeSeries(baselineData, projectedData));
    setTimeSeriesDataForCost({ baselineData, projectedData });
  }, []);

  // When Buildings BAU scenario changes, reset RNG to that scenario's default (user can override in Building Electrification panel)
  useEffect(() => {
    setBuildingsRngPercent(RNG_SHARE_2045_BY_SCENARIO[buildingsBauScenario] * 100);
  }, [buildingsBauScenario]);
  const [compactDevelopmentLoading, setCompactDevelopmentLoading] = useState<boolean>(false);

  const [compareMode, setCompareMode] = useState(false);
  const [perCapitaMode, setPerCapitaMode] = useState(false);
  const [panelALocation, setPanelALocation] = useState(location);
  const [panelAGeofips, setPanelAGeofips] = useState<number | null>(geofips);
  const [panelBLocation, setPanelBLocation] = useState(location);
  const [panelBGeofips, setPanelBGeofips] = useState<number | null>(geofips);

  useEffect(() => {
    setPanelALocation(location);
    setPanelAGeofips(geofips ?? null);
    setPanelBLocation(location);
    setPanelBGeofips(geofips ?? null);
  }, [location, geofips]);

  const handleCompareToggle = (checked: boolean) => {
    setCompareMode(checked);
    if (checked) {
      setPanelALocation((prev) => prev || location);
      setPanelAGeofips((prev) => prev ?? geofips ?? null);
      setPanelBLocation((prev) => prev || location);
      setPanelBGeofips((prev) => prev ?? geofips ?? null);
    }
  };

  const handlePanelLocationChange = async (panel: 'A' | 'B', newLocation: string) => {
    if (panel === 'A') {
      setPanelALocation(newLocation);
    } else {
      setPanelBLocation(newLocation);
    }
    try {
      const newGeofips = await supabaseLocationService.getGeofips(newLocation);
      if (panel === 'A') {
        setPanelAGeofips(newGeofips);
      } else {
        setPanelBGeofips(newGeofips);
      }
    } catch {
      if (panel === 'A') {
        setPanelAGeofips(null);
      } else {
        setPanelBGeofips(null);
      }
    }
  };

  // Compute parking-pricing-only VMT reduction percent (consistent with service logic)
  const computeParkingVMTReductionPercent = (assumptions: PolicyScenario['parkingPricing']): number => {
    if (assumptions.priceIncreasePercent === 0) return 0;
    const SF_COMMUTE_BY_CAR_PERCENT = 30.29;
    const BASE_ELASTICITY = -0.4;
    if (assumptions.commuteByCarPercent <= 0 || assumptions.commuteByCarPercent >= 100) return 0;
    const elasticity = BASE_ELASTICITY * (SF_COMMUTE_BY_CAR_PERCENT / assumptions.commuteByCarPercent);
    const vmtReductionPercent = Math.abs(elasticity) * (assumptions.priceIncreasePercent / 100);
    const effective = vmtReductionPercent * (assumptions.vmtAffectedPercent / 100) * 100; // percent
    return Math.max(0, Math.min(20, Number(effective.toFixed(1))));
  };

  // Update scenario's parking pricing priceIncreasePercent when menu slider changes
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      parkingPricing: {
        ...prev.parkingPricing,
        priceIncreasePercent: subPolicyValues.parkingPricing,
      },
    }));
  }, [subPolicyValues.parkingPricing]);

  // Calculate parking pricing and micromobility VMT reduction percentages
  const parkingVMTReductionPercent = computeParkingVMTReductionPercent(scenario.parkingPricing);

  // Headline VMT % = sum of modeled sub-policies (incl. compact VMT %) + stored Other Initiatives (set only when user moves the parent VMT slider).
  // Do not derive otherInitiatives from vmt − sum here: when compact drops, that was incorrectly freezing the headline and polluting otherInitiatives.
  useEffect(() => {
    const currentParkingVMT = computeParkingVMTReductionPercent({
      ...scenario.parkingPricing,
      priceIncreasePercent: subPolicyValues.parkingPricing,
    });
    const sumOfPolicyReductions =
      (subPolicyValues.activeTransportation || 0) +
      transitVMTReductionPercent +
      currentParkingVMT +
      micromobilityVMTReductionPercent +
      compactVmtReductionPercent;
    const other = Math.max(0, subPolicyValues.otherInitiatives ?? 0);
    const newTotal = Math.max(0, Math.min(20, sumOfPolicyReductions + other));
    setInterventionAreas((prev) => {
      if (Math.abs(prev.vmtReduction - newTotal) > 0.01) {
        return { ...prev, vmtReduction: newTotal };
      }
      return prev;
    });
  }, [
    scenario.parkingPricing,
    subPolicyValues.parkingPricing,
    subPolicyValues.otherInitiatives,
    transitVMTReductionPercent,
    micromobilityVMTReductionPercent,
    subPolicyValues.activeTransportation,
    compactVmtReductionPercent,
  ]);

  // Update scenario's vmtReductionPercent when interventionAreas.vmtReduction changes
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      vmtReductionPercent: interventionAreas.vmtReduction,
    }));
  }, [interventionAreas.vmtReduction]);

  // Sync scenario.downtownHomesPercent from subPolicyValues.downtownHomesPercent
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      downtownHomesPercent: subPolicyValues.downtownHomesPercent ?? 70,
    }));
  }, [subPolicyValues.downtownHomesPercent]);

  // Active transportation from scenario (URL / saved). Compact slider is housing growth % in subPolicy only;
  // scenario.vmtReductionComponents.transitOrientedDevelopment is incremental compact VMT % for GHG math.
  useEffect(() => {
    setSubPolicyValues((prev) => ({
      ...prev,
      activeTransportation: scenario.vmtReductionComponents.activeTransportation,
    }));
  }, [scenario.vmtReductionComponents.activeTransportation]);

  useEffect(() => {
    setScenario((prev) => {
      const nextTod = compactVmtReductionPercent;
      const nextAct = subPolicyValues.activeTransportation;
      if (
        prev.vmtReductionComponents.transitOrientedDevelopment === nextTod &&
        prev.vmtReductionComponents.activeTransportation === nextAct
      ) {
        return prev;
      }
      return {
        ...prev,
        vmtReductionComponents: {
          ...prev.vmtReductionComponents,
          transitOrientedDevelopment: nextTod,
          activeTransportation: nextAct,
        },
      };
    });
  }, [compactVmtReductionPercent, subPolicyValues.activeTransportation]);

  // Keep micromobility scale in scenario when slider changes
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      micromobility: {
        ...(prev.micromobility || {
          yearImplemented: 2025,
          tripsPerResident: 3,
          percentBike: 60,
          percentScooter: 40,
          milesPerBikeTrip: 1.61,
          milesPerScooterTrip: 1.2,
          percentBikeTripsDisplaceCars: 30,
          percentScooterTripsDisplaceCars: 20,
          kwhPerMileBike: 0.02,
          kwhPerMileScooter: 0.04,
          scalePercent: 0,
        }),
        scalePercent: subPolicyValues.micromobility || 0,
      },
    }));
  }, [subPolicyValues.micromobility]);

  // Compute Transit Service Improvements -> VMT reduction percent
  useEffect(() => {
    const increasePct = subPolicyValues.transitImprovements || 0; // % increase in service
    const pctRounded = calculateTransitVmtReductionPercent(increasePct);
    setTransitVMTReductionPercent(pctRounded);
    
    // Store the calculated VMT reduction % in the scenario (not the service increase %)
    setScenario(prev => ({
      ...prev,
      vmtReductionComponents: {
        ...prev.vmtReductionComponents,
        transitImprovements: pctRounded, // Store calculated VMT reduction %, not service increase %
      },
    }));
  }, [subPolicyValues.transitImprovements]);

  // Load micromobility detailed stats whenever scenario/location changes
  useEffect(() => {
    // Avoid carrying VMT % from a prior EMFAC baseline VMT denominator while results refresh.
    setMicromobilityVMTReductionPercent(0);
    const loadStats = async () => {
      if (!location) return;
      try {
        const stats = await getMicromobilityStats(location, {
          ...scenario,
          micromobility: {
            ...(scenario.micromobility || {
              yearImplemented: 2025,
              tripsPerResident: 3,
              percentBike: 60,
              percentScooter: 40,
              milesPerBikeTrip: 1.61,
              milesPerScooterTrip: 1.2,
              percentBikeTripsDisplaceCars: 30,
              percentScooterTripsDisplaceCars: 20,
              kwhPerMileBike: 0.02,
              kwhPerMileScooter: 0.04,
              scalePercent: 0,
            }),
            scalePercent: subPolicyValues.micromobility || 0,
          }
        } as PolicyScenario, emfacVersion);
        setMicromobilityStats(stats);
        
        // Calculate VMT reduction percentage from displaced car miles
        if (stats.displacedCarMiles > 0 && results?.baseline?.vmt) {
          const vmtReductionPercent = (stats.displacedCarMiles / results.baseline.vmt) * 100;
          setMicromobilityVMTReductionPercent(Math.max(0, Math.min(20, Number(vmtReductionPercent.toFixed(1)))));
        } else {
          setMicromobilityVMTReductionPercent(0);
        }
      } catch (e) {
        setMicromobilityStats(null);
        setMicromobilityVMTReductionPercent(0);
      }
    };
    if (results?.baseline?.vmt) {
      loadStats();
    }
  }, [location, scenario, subPolicyValues.micromobility, results?.baseline?.vmt, emfacVersion]);

  // Load baseline transit revenue miles (vehicle revenue miles) for the selected location
  useEffect(() => {
    const loadTransitMiles = async () => {
      try {
        // Prefer geofips if provided; fallback to searching by name
        let row: Record<string, unknown> | null = null;
        if (geofips) {
          const { data } = await supabaseClient
            .from('vehicle_revenue_miles')
            .select('*')
            .eq('geofips', String(geofips))
            .maybeSingle();
          row = (data as Record<string, unknown> | null) || null;
        }
        if (!row) {
          const { data } = await supabaseClient
            .from('vehicle_revenue_miles')
            .select('*')
            .ilike('geo_name', `%${location}%`)
            .in('location_type', ['City', 'Place'])
            .limit(1)
            .maybeSingle();
          row = (data as Record<string, unknown> | null) || null;
        }

        if (row) {
          const history = Object.entries(row)
            .map(([key, rawValue]) => {
              if (!/^\d{4}$/.test(key)) return null;
              const year = Number(key);
              const miles = Number(String(rawValue ?? '').replace(/,/g, ''));
              if (!Number.isFinite(year) || !Number.isFinite(miles) || miles <= 0) return null;
              return { year, miles };
            })
            .filter((point): point is { year: number; miles: number } => point !== null)
            .sort((a, b) => a.year - b.year);

          setTransitServiceHistory(history);

          const latestThrough2023 = [...history].reverse().find((point) => point.year <= 2023);
          const latestAvailable = latestThrough2023 || history[history.length - 1];
          if (latestAvailable) {
            setTransitRevenueMiles(latestAvailable);
            return;
          }
        }

        setTransitServiceHistory([]);
        setTransitRevenueMiles(null);
      } catch (e) {
        setTransitServiceHistory([]);
        setTransitRevenueMiles(null);
      }
    };
    loadTransitMiles();
  }, [location, geofips]);

  // Load compact development whenever geofips is set (housing trajectory, charts, BAU % default). Slider = growth override for series/GHG.
  useEffect(() => {
    if (!geofips || !Number.isFinite(geofips)) {
      setCompactDevelopmentSeries(null);
      prevCompactFetchGeofipsRef.current = null;
      return;
    }
    if (prevCompactFetchGeofipsRef.current !== geofips) {
      setCompactDevelopmentSeries(null);
      prevCompactFetchGeofipsRef.current = geofips;
    }
    let cancelled = false;
    setCompactDevelopmentLoading(true);
    const startYear = 2015;
    const endYear = 2045;
    const growthOverride = typeof subPolicyValues.transitOrientedDevelopment === 'number' ? subPolicyValues.transitOrientedDevelopment : undefined;
    fetchCompactDevelopmentSeries(geofips, startYear, endYear, 'incremental_baseline', growthOverride, location)
      .then((series) => {
        if (!cancelled) {
          setCompactDevelopmentSeries(series ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setCompactDevelopmentSeries(null);
      })
      .finally(() => {
        if (!cancelled) setCompactDevelopmentLoading(false);
      });
    return () => { cancelled = true; };
  }, [geofips, location, subPolicyValues.transitOrientedDevelopment]);

  // Keep compact growth ≥ location BAU floor (slider left = population-tracking growth), unless URL/saved explicitly opted out.
  useEffect(() => {
    if (!compactDevelopmentSeries || !Number.isFinite(compactDevelopmentSeries.bauGrowthPercent)) return;
    if (compactAllowBelowBauGrowth || urlHadCompactDevelopmentParamRef.current || savedScenarioLoadedFromUrlRef.current) {
      return;
    }
    const floor =
      Math.round(Math.min(GROWTH_RATE_PERCENT_MAX, Math.max(0, compactDevelopmentSeries.bauGrowthPercent)) * 10) / 10;
    setSubPolicyValues((prev) => {
      const v = prev.transitOrientedDevelopment ?? 0;
      if (v >= floor - 1e-9) return prev;
      return { ...prev, transitOrientedDevelopment: floor };
    });
  }, [compactDevelopmentSeries, compactAllowBelowBauGrowth]);

  // Vehicle Electrification slider is beyond-BAU (0 = EMFAC BAU 2045 EV%, 100 = 100% ZEV).
  // Default UI value is 0; absolute fleet EV% is derived when baseline BAU is known.
  const evDefaultInitializedRef = React.useRef(false);
  useEffect(() => {
    const bau2045 = results?.baseline.currentEVPercent;
    if (bau2045 === undefined || !Number.isFinite(bau2045)) return;

    // Migrate legacy absolute EV from URL / old saved scenarios once.
    if (!legacyEvMigratedRef.current) {
      const legacyAbs =
        pendingLegacyAbsoluteEvRef.current != null
          ? pendingLegacyAbsoluteEvRef.current
          : pendingSavedAbsoluteEvRef.current;
      if (legacyAbs != null) {
        legacyEvMigratedRef.current = true;
        pendingLegacyAbsoluteEvRef.current = null;
        pendingSavedAbsoluteEvRef.current = null;
        const beyond = absoluteEvToBeyondBauSlider(legacyAbs, bau2045);
        setInterventionAreas((prev) =>
          Math.abs(prev.vehicleElectrification - beyond) < 1e-6
            ? prev
            : { ...prev, vehicleElectrification: beyond }
        );
        setScenario((prev) => {
          const absolute = beyondBauEvSliderToAbsolute(beyond, bau2045);
          return prev.evAdoptionPercent === absolute ? prev : { ...prev, evAdoptionPercent: absolute };
        });
        evDefaultInitializedRef.current = true;
        return;
      }
      legacyEvMigratedRef.current = true;
    }

    if (evDefaultInitializedRef.current || savedScenarioLoadedFromUrlRef.current) return;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.has('ev') || params.has('scenarioId')) {
        evDefaultInitializedRef.current = true;
        return;
      }
    }
    evDefaultInitializedRef.current = true;
    setInterventionAreas((prev) =>
      prev.vehicleElectrification !== 0 ? { ...prev, vehicleElectrification: 0 } : prev
    );
    setScenario((prev) =>
      prev.evAdoptionPercent !== bau2045 ? { ...prev, evAdoptionPercent: bau2045 } : prev
    );
  }, [results?.baseline.currentEVPercent]);

  // Default grid decarbonization lever is now the clean electricity year (SB 100 default: 2045).
  useEffect(() => {
    setInterventionAreas((prev) => ({
      ...prev,
      gridDecarbonization: prev.gridDecarbonization || 2045,
    }));
    setScenario((prev) => ({
      ...prev,
      gridCleanElectricityYear: (prev as any).gridCleanElectricityYear || 2045,
    } as any));
  }, []);

  // Sync beyond-BAU EV slider → absolute 2045 fleet EV% for the policy model.
  useEffect(() => {
    const bau2045 = results?.baseline.currentEVPercent;
    if (bau2045 === undefined || !Number.isFinite(bau2045)) return;
    const absolute = beyondBauEvSliderToAbsolute(interventionAreas.vehicleElectrification, bau2045);
    setScenario((prev) =>
      Math.abs(prev.evAdoptionPercent - absolute) < 1e-6
        ? prev
        : { ...prev, evAdoptionPercent: absolute }
    );
  }, [interventionAreas.vehicleElectrification, results?.baseline.currentEVPercent]);

  // Sync Energy Efficiency (collapsed): headline sets combined target; named levers stay fixed; other absorbs the gap (VMT-style).
  useEffect(() => {
    if (expandedIntervention === 'energyEfficiency') return;
    const effRaw = Math.max(0, Math.min(40, interventionAreas.energyEfficiency ?? 0));
    const bauT = results?.baseline?.bauTherms2045 ?? 1;
    const bauK = results?.baseline?.bauKwh2045 ?? 1;

    let headlineAdjust: number | undefined;

    setScenario(prev => {
      const current = prev.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents();
      let effOut = effRaw;
      let nextComponents;

      if (effRaw <= 0) {
        nextComponents = getDefaultEnergyEfficiencyComponents();
        effOut = 0;
      } else {
        const named = {
          residentialReachCodePercent: current.residentialReachCodePercent ?? 0,
          residentialRetrofitExistingPercent: current.residentialRetrofitExistingPercent ?? 0,
          commercialRetrofitExistingPercent: current.commercialRetrofitExistingPercent ?? 0,
        };
        const { otherEfficiencyPercent, achievableTarget } = solveOtherEfficiencyPercentForCombinedTarget(
          effRaw,
          named,
          bauT,
          bauK,
          efficiencyLayerOptions
        );
        effOut = Math.max(0, Math.min(40, achievableTarget));
        nextComponents = { ...named, otherEfficiencyPercent };
      }

      const c = prev.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents();
      const unchanged =
        (prev.energyEfficiencyTargetPercent ?? 0) === effOut &&
        (prev.energyEfficiencyPercent ?? 0) === effOut &&
        (c.residentialReachCodePercent ?? 0) === (nextComponents.residentialReachCodePercent ?? 0) &&
        (c.residentialRetrofitExistingPercent ?? 0) === (nextComponents.residentialRetrofitExistingPercent ?? 0) &&
        (c.commercialRetrofitExistingPercent ?? 0) === (nextComponents.commercialRetrofitExistingPercent ?? 0) &&
        (c.otherEfficiencyPercent ?? 0) === (nextComponents.otherEfficiencyPercent ?? 0);
      if (unchanged) return prev;

      if (Math.abs(effOut - effRaw) > 0.05) {
        headlineAdjust = effOut;
      }

      return {
        ...prev,
        energyEfficiencyTargetPercent: effOut,
        energyEfficiencyComponents: nextComponents,
        energyEfficiencyPercent: effOut,
      };
    });

    if (headlineAdjust !== undefined) {
      setInterventionAreas(p =>
        Math.abs((p.energyEfficiency ?? 0) - headlineAdjust!) > 0.05 ? { ...p, energyEfficiency: headlineAdjust! } : p
      );
    }
  }, [
    interventionAreas.energyEfficiency,
    expandedIntervention,
    results?.baseline?.bauKwh2045,
    results?.baseline?.bauTherms2045,
    efficiencyLayerOptions,
  ]);

  // Sync Building Electrification slider with scenario heatPumpAdoptionPercent (NG reduction; electricity impact later).
  // When Energy Efficiency slider is 0, keep scenario efficiency at 0 so "only building electrification" gives correct cost (policy elec > BAU, negative operating savings).
  useEffect(() => {
    const bldgElec = Math.max(0, Math.min(100, interventionAreas.buildingsElectrification || 0));
    const eff = interventionAreas.energyEfficiency ?? 0;
    setScenario(prev => {
      const next = { ...prev, heatPumpAdoptionPercent: bldgElec };
      if (eff === 0) {
        next.energyEfficiencyTargetPercent = 0;
        next.energyEfficiencyPercent = 0;
        next.energyEfficiencyComponents = getDefaultEnergyEfficiencyComponents();
      }
      return next;
    });
  }, [interventionAreas.buildingsElectrification, interventionAreas.energyEfficiency]);

  // Sync Organics Diversion slider with scenario organicsDiversionPercent (0-50 additional beyond BAU)
  useEffect(() => {
    // Organics is LOCAL incremental beyond BAU (0–50 percentage points by 2045).
    const organics = Math.max(0, Math.min(50, interventionAreas.organicsDiversion || 0));
    setScenario(prev => ({
      ...prev,
      organicsDiversionPercent: organics,
    }));
  }, [interventionAreas.organicsDiversion]);

  // Sync Grid Decarbonization slider (year) with scenario.gridCleanElectricityYear
  useEffect(() => {
    const raw = Number(interventionAreas.gridDecarbonization || 2045);
    const clamped = Math.max(2025, Math.min(2045, raw));
    setScenario(prev => ({
      ...prev,
      gridCleanElectricityYear: clamped,
    } as any));
  }, [interventionAreas.gridDecarbonization]);

  // When Energy Efficiency main slider > 0 or panel expanded, compute chart data and segment shares from cached baseline
  useEffect(() => {
    const showEfficiencyCharts = expandedIntervention === 'energyEfficiency';
    if (!showEfficiencyCharts || !location) {
      setEfficiencyChartData(null);
      setEfficiencyContributions2045(null);
      setEfficiencySegmentShares(null);
      return;
    }
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    const pathway = baseline?.buildingsBauPathway;
    if (!pathway?.years?.length || !pathway.thermsByYear?.length || !pathway.kwhByYear?.length) {
      setEfficiencyChartData(null);
      setEfficiencyContributions2045(null);
      setEfficiencySegmentShares(null);
      return;
    }
    const years = pathway.years.filter((y) => y >= 2023 && y <= 2045);
    const bauThermsByYear = years.map((y) => getPathwayTherms(pathway, y));
    const bauKwhByYear = years.map((y) => getPathwayKwh(pathway, y));
    const components = scenario.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents();
    const result = applyEfficiencyLayer(bauThermsByYear, bauKwhByYear, years, components, efficiencyLayerOptions);
    setEfficiencyChartData({
      years,
      bauTherms: bauThermsByYear,
      bauKwh: bauKwhByYear,
      effTherms: result.thermsByYear,
      effKwh: result.kwhByYear,
    });
    const idx2045 = years.indexOf(2045);
    if (idx2045 >= 0) {
      const withContrib = applyEfficiencyForYearWithContributions(
        bauThermsByYear[idx2045] ?? 0,
        bauKwhByYear[idx2045] ?? 0,
        2045,
        components,
        efficiencyLayerOptions
      );
      setEfficiencyContributions2045(withContrib.contributions);
    } else {
      setEfficiencyContributions2045(null);
    }
    const resExisting2030 = efficiencyLayerOptions?.existingResShareByYear?.(2030) ?? 1 / Math.pow(1.02, 2030 - 2023);
    const resExisting2045 = efficiencyLayerOptions?.existingResShareByYear?.(2045) ?? 1 / Math.pow(1.02, 2045 - 2023);
    const comExisting2030 = efficiencyLayerOptions?.existingComShareByYear?.(2030) ?? 1 / Math.pow(1 + COMMERCIAL_ANNUAL_GROWTH_RATE, 2030 - 2023);
    const comExisting2045 = efficiencyLayerOptions?.existingComShareByYear?.(2045) ?? 1 / Math.pow(1 + COMMERCIAL_ANNUAL_GROWTH_RATE, 2045 - 2023);
    setEfficiencySegmentShares({
      resExisting2030,
      resExisting2045,
      comExisting2030,
      comExisting2045,
    });
  }, [expandedIntervention, interventionAreas.energyEfficiency, location, emfacVersion, buildingsBauScenario, scenario, efficiencyLayerOptions]);

  /** BAU vs policy building therms/kWh for Building Electrification panel charts (matches cost / bill pipeline). */
  const buildingEnergyComparisonSeries: BuildingEnergyComparisonSeries | null = useMemo(() => {
    const baselineCacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(baselineCacheKey);
    if (!baseline) return null;
    try {
      const energy = getBuildingEnergyTimeSeries(baseline, scenario, {
        rngOverridePercent: buildingsRngPercent,
        cop: buildingsCop,
        efficiencyLayerOptions,
      });
      const years = energy.years.filter((y) => y >= CHART_YEAR_MIN && y <= CHART_YEAR_MAX);
      const yi = (y: number) => energy.years.indexOf(y);
      return {
        labels: years.map(String),
        bauTherms: years.map((y) => energy.bauThermsByYear[yi(y)] ?? 0),
        bauKwh: years.map((y) => energy.bauKwhByYear[yi(y)] ?? 0),
        policyTherms: years.map((y) => energy.policyThermsByYear[yi(y)] ?? 0),
        policyKwh: years.map((y) => energy.policyKwhByYear[yi(y)] ?? 0),
      };
    } catch {
      return null;
    }
  }, [location, emfacVersion, buildingsBauScenario, scenario, buildingsRngPercent, buildingsCop, efficiencyLayerOptions]);

  /** Buildings cost layer: uses cached baseline + time series; costView selects BAU vs Local vs Total. */
  const buildingsCostResults = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    const flatBaselineData = portfolioCostView !== 'incremental_vs_bau' ? generateFlatBaselineTimeSeries(baseline) : null;
    const flatPathway = portfolioCostView !== 'incremental_vs_bau' ? getFlatBuildingsPathway(baseline) : undefined;
    let emissionsResults = timeSeriesDataForCost;
    if (portfolioCostView === 'bau_costs' && flatBaselineData) {
      emissionsResults = { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.baselineData };
    } else if (portfolioCostView === 'total_costs' && flatBaselineData) {
      emissionsResults = { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.projectedData };
    }
    const out = computeBuildingsCostResults({
      location,
      baseline,
      policyScenario: scenario,
      buildingsBauScenario,
      buildingsRngPercentOverride: buildingsRngPercent,
      buildingsCop,
      efficiencyLayerOptions,
      emissionsResults,
      referencePathway: flatPathway,
      comparisonTarget: portfolioCostView === 'bau_costs' ? 'bau' : portfolioCostView === 'total_costs' ? 'policy' : undefined,
      housingUnitsByYear: housingUnitsByYearForPolicyPath ?? undefined,
      commercialEstablishmentCount: commercialEstablishmentCount ?? undefined,
      assumptions: policyBuildingsCostAssumptions,
    });
    return out;
  }, [location, emfacVersion, buildingsBauScenario, scenario, buildingsRngPercent, buildingsCop, efficiencyLayerOptions, timeSeriesDataForCost, portfolioCostView, housingUnitsByYearForPolicyPath, commercialEstablishmentCount, policyBuildingsCostAssumptions]);

  /** Transport cost for current location; uses same cost view as buildings (flat vs BAU vs policy). */
  const transportCostResults = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    const flatBaselineData = portfolioCostView !== 'incremental_vs_bau' ? generateFlatBaselineTimeSeries(baseline) : null;
    let transportTimeSeries = timeSeriesDataForCost;
    if (portfolioCostView === 'bau_costs' && flatBaselineData) {
      transportTimeSeries = { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.baselineData };
    } else if (portfolioCostView === 'total_costs' && flatBaselineData) {
      transportTimeSeries = { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.projectedData };
    }
    const transportScenario =
      portfolioCostView === 'bau_costs'
        ? { ...scenario, evAdoptionPercent: baseline.currentEVPercent2045 ?? 20, vmtReductionPercent: 0 }
        : scenario;
    return computeTransportCostResults({
      baseline,
      timeSeries: transportTimeSeries,
      scenario: transportScenario,
      options: { ...policyTransportCostOptionsBase, costView: portfolioCostView },
    });
  }, [location, emfacVersion, buildingsBauScenario, scenario, timeSeriesDataForCost, portfolioCostView, policyTransportCostOptionsBase]);

  // Cost decomposition for Decision Summary narrative:
  // - BAU/state-policy-only costs
  // - Local incremental costs above BAU
  const buildingsCostBau = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    const flatBaselineData = generateFlatBaselineTimeSeries(baseline);
    const flatPathway = getFlatBuildingsPathway(baseline);
    return computeBuildingsCostResults({
      location,
      baseline,
      policyScenario: scenario,
      buildingsBauScenario,
      buildingsRngPercentOverride: buildingsRngPercent,
      buildingsCop,
      efficiencyLayerOptions,
      emissionsResults: { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.baselineData },
      referencePathway: flatPathway,
      comparisonTarget: 'bau',
      housingUnitsByYear: housingUnitsByYearForPolicyPath ?? undefined,
      commercialEstablishmentCount: commercialEstablishmentCount ?? undefined,
      assumptions: policyBuildingsCostAssumptions,
    });
  }, [location, emfacVersion, buildingsBauScenario, scenario, buildingsRngPercent, buildingsCop, efficiencyLayerOptions, timeSeriesDataForCost, housingUnitsByYearForPolicyPath, commercialEstablishmentCount, policyBuildingsCostAssumptions]);

  const transportCostBau = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    const flatBaselineData = generateFlatBaselineTimeSeries(baseline);
    return computeTransportCostResults({
      baseline,
      timeSeries: { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.baselineData },
      scenario: { ...scenario, evAdoptionPercent: baseline.currentEVPercent2045 ?? 20, vmtReductionPercent: 0 },
      options: { ...policyTransportCostOptionsBase, costView: 'bau_costs' },
    });
  }, [location, emfacVersion, buildingsBauScenario, scenario, timeSeriesDataForCost, policyTransportCostOptionsBase]);

  const buildingsCostLocalIncremental = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    return computeBuildingsCostResults({
      location,
      baseline,
      policyScenario: scenario,
      buildingsBauScenario,
      buildingsRngPercentOverride: buildingsRngPercent,
      buildingsCop,
      efficiencyLayerOptions,
      emissionsResults: timeSeriesDataForCost,
      housingUnitsByYear: housingUnitsByYearForPolicyPath ?? undefined,
      commercialEstablishmentCount: commercialEstablishmentCount ?? undefined,
      assumptions: policyBuildingsCostAssumptions,
    });
  }, [location, emfacVersion, buildingsBauScenario, scenario, buildingsRngPercent, buildingsCop, efficiencyLayerOptions, timeSeriesDataForCost, housingUnitsByYearForPolicyPath, commercialEstablishmentCount, policyBuildingsCostAssumptions]);

  const transportCostLocalIncremental = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    return computeTransportCostResults({
      baseline,
      timeSeries: timeSeriesDataForCost,
      scenario,
      options: { ...policyTransportCostOptionsBase, costView: 'incremental_vs_bau' },
    });
  }, [location, emfacVersion, buildingsBauScenario, scenario, timeSeriesDataForCost, policyTransportCostOptionsBase]);

  // Fetch housing units by year for building electrification CAPEX ($/unit) when geofips available.
  useEffect(() => {
    if (!geofips || !Number.isFinite(geofips)) {
      setHousingUnitsByYear(null);
      return;
    }
    let cancelled = false;
    fetchHousingUnitsByYear(geofips, 2023, 2045).then((byYear) => {
      if (!cancelled) setHousingUnitsByYear(byYear);
    }).catch(() => {
      if (!cancelled) setHousingUnitsByYear(null);
    });
    return () => { cancelled = true; };
  }, [geofips]);

  useEffect(() => {
    if (geofips == null || !Number.isFinite(geofips)) {
      setCommercialEstablishmentCount(null);
      return;
    }
    let cancelled = false;
    fetchLatestTotalEstablishmentsForGeofips(geofips)
      .then((n) => {
        if (!cancelled) setCommercialEstablishmentCount(n);
      })
      .catch(() => {
        if (!cancelled) setCommercialEstablishmentCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [geofips]);

  // Commercial gas end-use % from CBECS 2018 + CBP-weighted table (when user has not customized defaults).
  useEffect(() => {
    if (!urlScenarioHydrated) return;
    if (geofips == null || !Number.isFinite(geofips)) return;
    let cancelled = false;
    fetchCommercialGasEndUseResolved(geofips).then((row) => {
      if (cancelled || !row) return;
      setScenario((prev) => {
        if (!commercialSharesNearDefaults(prev.buildingEndUseAssumptions?.commercial)) {
          return prev;
        }
        return {
          ...prev,
          buildingEndUseAssumptions: {
            ...getDefaultBuildingEndUseAssumptions(),
            ...prev.buildingEndUseAssumptions,
            commercial: {
              spaceHeating: row.spaceHeating,
              waterHeating: row.waterHeating,
              cooking: row.cooking,
              other: row.other,
            },
          },
        };
      });
    });
    return () => {
      cancelled = true;
    };
  }, [geofips, urlScenarioHydrated]);

  // GHG stacked-chart target line: defaults from California CAP Database (Supabase) when not loading a saved scenario from URL.
  // When official_baseline_year is set, the plan baseline was before 2005; we show 2005 in the reference field and explain in banner + input title.
  useEffect(() => {
    if (!urlScenarioHydrated) return;
    if (geofips == null || !Number.isFinite(geofips)) {
      setCapTargetDefaultsBanner(null);
      setCapReferenceYearSubstitutionHint(undefined);
      return;
    }
    if (savedScenarioLoadedFromUrlRef.current) {
      setCapTargetDefaultsBanner(null);
      setCapReferenceYearSubstitutionHint(undefined);
      return;
    }

    const DEFAULT_RED = 80;
    const DEFAULT_BASE = 2015;
    const DEFAULT_END = 2045;

    let cancelled = false;
    (async () => {
      const row = await fetchCaliforniaCapGhgTargetForGeofips(geofips);
      if (cancelled) return;

      if (!row) {
        setCapTargetDefaultsBanner(null);
        setCapReferenceYearSubstitutionHint(undefined);
        setTargetReductionPercent(DEFAULT_RED);
        setTargetBaselineYear(DEFAULT_BASE);
        setTargetEndYear(DEFAULT_END);
        return;
      }

      const adoptYear = parseCapAdoptionDisplayYear(row.adoption_year);
      let appliedFromCap = false;

      if (row.percent_below != null && row.percent_below !== '') {
        const p = Number(row.percent_below);
        if (Number.isFinite(p)) {
          setTargetReductionPercent(clampInt(Math.round(p), 0, 100));
          appliedFromCap = true;
        }
      }
      if (row.baseline_year != null && Number.isFinite(Number(row.baseline_year))) {
        setTargetBaselineYear(
          clampInt(Number(row.baseline_year), TARGET_REF_YEAR_MIN, TARGET_REF_YEAR_MAX)
        );
        appliedFromCap = true;
      }
      const endRaw = row.target_year;
      if (endRaw != null && Number.isFinite(Number(endRaw))) {
        setTargetEndYear(clampInt(Number(endRaw), TARGET_END_YEAR_MIN, TARGET_END_YEAR_MAX));
        appliedFromCap = true;
      }

      if (!appliedFromCap) {
        setTargetReductionPercent(DEFAULT_RED);
        setTargetBaselineYear(DEFAULT_BASE);
        setTargetEndYear(DEFAULT_END);
        setCapReferenceYearSubstitutionHint(undefined);
        setCapTargetDefaultsBanner(
          capTargetDefaultsBubbleBodyWithCitation(
            adoptYear
              ? `A Climate Action Plan for this jurisdiction is on file (adopted in ${adoptYear}), but its GHG targets could not be mapped to these controls. The dashed target line uses generic defaults (${DEFAULT_RED}% below ${DEFAULT_BASE} levels by ${DEFAULT_END}).`
              : `A Climate Action Plan for this jurisdiction is on file, but its GHG targets could not be mapped to these controls. The dashed target line uses generic defaults (${DEFAULT_RED}% below ${DEFAULT_BASE} levels by ${DEFAULT_END}).`
          )
        );
        return;
      }

      const ob = row.official_baseline_year;
      const officialBaseline = ob != null && Number.isFinite(Number(ob)) ? Math.round(Number(ob)) : null;
      if (officialBaseline != null) {
        setCapReferenceYearSubstitutionHint(
          `The Climate Action Plan’s official baseline year is ${officialBaseline}. This field uses 2005 instead so it matches the GHG inventory time series.`
        );
      } else {
        setCapReferenceYearSubstitutionHint(undefined);
      }

      const planBaselineForCarbonNeutralSentence =
        officialBaseline ??
        (row.baseline_year != null && Number.isFinite(Number(row.baseline_year))
          ? Math.round(Number(row.baseline_year))
          : null);
      const carbonNeutralEndYear =
        row.target_year != null && Number.isFinite(Number(row.target_year))
          ? Math.round(Number(row.target_year))
          : null;
      const carbonNeutralNote =
        row.target_source === 'carbon_neutral' &&
        planBaselineForCarbonNeutralSentence != null &&
        carbonNeutralEndYear != null
          ? ` When the CAP lists a carbon-neutral year, it takes priority over GHG Target 1–3 in the database. The stated goal is 100% below ${planBaselineForCarbonNeutralSentence} levels by ${carbonNeutralEndYear}.`
          : '';

      const pre2005BaselineNote =
        officialBaseline != null
          ? ` Note: The Climate Action Plan’s official baseline year is ${officialBaseline}; this tool uses 2005 as the reference year instead, so the dashed target line lines up with the inventory time series (which begins in 2005).`
          : '';

      if (adoptYear) {
        setCapTargetDefaultsBanner(
          capTargetDefaultsBubbleBodyWithCitation(
            `Default target line values (% reduction, reference year, and end year) are set from this jurisdiction’s Climate Action Plan adopted in ${adoptYear}. You can change them above anytime.${carbonNeutralNote}${pre2005BaselineNote}`
          )
        );
      } else {
        setCapTargetDefaultsBanner(
          capTargetDefaultsBubbleBodyWithCitation(
            `Default target line values are set from this jurisdiction’s Climate Action Plan (California CAP Database). You can change them above anytime.${carbonNeutralNote}${pre2005BaselineNote}`
          )
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [geofips, urlScenarioHydrated]);

  // Resolve location-specific electricity price when available; fallback to assumptions default.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!geofips || !Number.isFinite(geofips)) {
        setElectricityPriceContext({
          pricePerKwh: ELECTRICITY_PRICE_PER_KWH,
          source: 'Default building cost assumption',
        });
        return;
      }
      try {
        const price = await fetchLatestElectricityPricesForLocation(geofips);
        if (cancelled) return;
        const resolved = price.buildingPricePerKwh ?? price.average ?? ELECTRICITY_PRICE_PER_KWH;
        const source = price.utilityName
          ? `Supabase utility price (${price.utilityName}${price.year ? `, ${price.year}` : ''})`
          : `Supabase location price${price.year ? ` (${price.year})` : ''}`;
        setElectricityPriceContext({ pricePerKwh: resolved, source });
      } catch {
        if (cancelled) return;
        setElectricityPriceContext({
          pricePerKwh: ELECTRICITY_PRICE_PER_KWH,
          source: 'Default building cost assumption (Supabase price unavailable)',
        });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [geofips]);

  // Ensure time series is available for cost when results exist. Refetch when scenario (or other cost-relevant params) changes
  // so buildings/transport cost charts stay in sync with sliders (fixes phantom spikes when only Vehicle Electrification changes).
  useEffect(() => {
    if (!location || !results) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await generatePolicyTimeSeriesData(location, scenario, emfacVersion, buildingsBauScenario, efficiencyLayerOptions ? { efficiencyLayerOptions } : undefined);
        if (!cancelled && data?.baselineData?.length && data?.projectedData?.length) {
          setTimeSeriesDataForCost({ baselineData: data.baselineData, projectedData: data.projectedData });
        }
      } catch (_) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [location, results, scenario, emfacVersion, buildingsBauScenario, efficiencyLayerOptions]);


  const compactDevelopmentImpactKey = useMemo(() => {
    const rows = compactDevelopmentSeries?.rows;
    if (!rows?.length) return '';
    return rows.map((r) => `${r.year ?? ''}:${r.netStatewideAnnual ?? ''}`).join('|');
  }, [compactDevelopmentSeries]);

  /** Inputs that require inventory/baseline/EMFAC version/buildings pathway recalculation — not policy sliders. */
  const impactHeavyDepsKey = useMemo(
    () =>
      [
        location ?? '',
        emfacVersion,
        buildingsBauScenario,
        String(buildingsRngPercent ?? ''),
        String(buildingsCop ?? ''),
        compactDevelopmentImpactKey,
        JSON.stringify(efficiencyLayerOptions ?? null),
      ].join('\u001f'),
    [
      location,
      emfacVersion,
      buildingsBauScenario,
      buildingsRngPercent,
      buildingsCop,
      compactDevelopmentImpactKey,
      efficiencyLayerOptions,
    ]
  );

  // Calculate impact whenever scenario or location changes (debounced; stale async results ignored).
  // EMFAC / Buildings BAU results are retained in getCachedPolicyImpactResults so switching back is instant.
  useEffect(() => {
    let cancelled = false;

    const policyYear = 2045;
    const compactNetSigned =
      compactDevelopmentSeries?.rows?.find((r) => r.year === policyYear)?.netStatewideAnnual ??
      compactDevelopmentSeries?.rows?.slice(-1)[0]?.netStatewideAnnual;
    const compactReductionMt = ldvReductionMtFromNetStatewideAnnual(compactNetSigned);
    const impactOptions = {
      ...(compactReductionMt > 0 ? { compactDevelopmentLdvReductionMT: compactReductionMt } : {}),
      buildingsBauScenario,
      buildingsRngPercentOverride: buildingsRngPercent,
      buildingsCopOverride: buildingsCop,
      ...(efficiencyLayerOptions ? { efficiencyLayerOptions } : {}),
    };

    if (location) {
      const cachedImpact = getCachedPolicyImpactResults(location, scenario, emfacVersion, impactOptions);
      if (cachedImpact) {
        setResults(cachedImpact);
        lastCompletedImpactHeavyKeyRef.current = impactHeavyDepsKey;
        setLoading(false);
        setLoadProgress(null);
        setError(null);
        return;
      }
    }

    // Cache miss on EMFAC/BAU (or first load): block UI immediately so we don't flash the prior version's chart.
    const needsBlockingLoading =
      resultsRef.current == null || impactHeavyDepsKey !== lastCompletedImpactHeavyKeyRef.current;
    if (needsBlockingLoading) {
      setLoading(true);
      setLoadProgress(null);
    }

    const timeoutId = window.setTimeout(() => {
      void (async () => {
        if (!location || cancelled) return;
        setError(null);
        try {
          const impact = await calculatePolicyImpact(location, scenario, emfacVersion, {
            ...impactOptions,
            onCalculationProgress: (p: PolicyLoadProgress) => {
              if (!cancelled) setLoadProgress(p);
            },
          });
          if (!cancelled) {
            setResults(impact);
            lastCompletedImpactHeavyKeyRef.current = impactHeavyDepsKey;
          }
        } catch (err) {
          if (!cancelled) {
            console.error('Error calculating policy impact:', err);
            setError('Failed to calculate policy impact. Please try again.');
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
            setLoadProgress(null);
          }
        }
      })();
    }, needsBlockingLoading ? 0 : 500);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    location,
    scenario,
    emfacVersion,
    buildingsBauScenario,
    buildingsRngPercent,
    buildingsCop,
    compactDevelopmentSeries,
    efficiencyLayerOptions,
    impactHeavyDepsKey,
  ]);

  const handleLoadPredefinedScenario = (scenarioName: string) => {
    if (
      scenarioName !== 'baseline' &&
      scenarioName !== 'moderate' &&
      scenarioName !== 'aggressive' &&
      scenarioName !== 'fullImplementation' &&
      scenarioName !== 'electrificationFocus'
    ) {
      return;
    }
    const predefined = getPredefinedScenarios();
    const preset = predefined[scenarioName as keyof typeof predefined];
    if (!preset) return;

    const loadedScenario = { ...preset };
    if (scenarioName === 'electrificationFocus' && emfacVersion === '2021') {
      loadedScenario.evAdoptionPercent = 70;
    }

    // Preset `evAdoptionPercent` values are beyond-BAU ambition (0=BAU … 100=100% ZEV).
    const beyondBauEv = Math.max(0, Math.min(100, loadedScenario.evAdoptionPercent));
    const bau2045 = results?.baseline.currentEVPercent;
    const absoluteEv =
      bau2045 != null && Number.isFinite(bau2045)
        ? beyondBauEvSliderToAbsolute(beyondBauEv, bau2045)
        : beyondBauEv;

    setLocalScenarioName(scenarioName as LocalPresetParam);
    setScenario({
      ...loadedScenario,
      evAdoptionPercent: absoluteEv,
      vmtReductionComponents: {
        ...loadedScenario.vmtReductionComponents,
        transitOrientedDevelopment: 0,
      },
    });
    const effTarget = loadedScenario.energyEfficiencyTargetPercent ?? loadedScenario.energyEfficiencyPercent ?? 0;
    setInterventionAreas({
      urbanInfill: 0,
      vmtReduction: loadedScenario.vmtReductionPercent,
      vehicleElectrification: beyondBauEv,
      energyEfficiency: effTarget,
      buildingsElectrification: loadedScenario.heatPumpAdoptionPercent,
      gridDecarbonization: loadedScenario.gridCleanElectricityYear,
      organicsDiversion: loadedScenario.organicsDiversionPercent,
    });
    const subPolicySum =
      (loadedScenario.vmtReductionComponents.transitOrientedDevelopment || 0) +
      (loadedScenario.vmtReductionComponents.activeTransportation || 0);
    const otherInit = Math.max(0, loadedScenario.vmtReductionPercent - subPolicySum);
    setSubPolicyValues((prev) => ({
      ...prev,
      transitOrientedDevelopment: loadedScenario.vmtReductionComponents.transitOrientedDevelopment,
      downtownHomesPercent: loadedScenario.downtownHomesPercent ?? 70,
      activeTransportation: loadedScenario.vmtReductionComponents.activeTransportation,
      transitImprovements: loadedScenario.vmtReductionComponents.transitImprovements,
      parkingPricing: loadedScenario.parkingPricing.priceIncreasePercent,
      micromobility: loadedScenario.micromobility?.scalePercent || 0,
      otherInitiatives: otherInit,
    }));
  };

  const handleResetScenario = () => {
    setLocalScenarioName('baseline');
    const defaultScenario = getDefaultScenario();
    const bau2045 = results?.baseline.currentEVPercent;
    setScenario({
      ...defaultScenario,
      evAdoptionPercent: bau2045 != null && Number.isFinite(bau2045) ? bau2045 : 0,
      vmtReductionComponents: {
        ...defaultScenario.vmtReductionComponents,
        transitOrientedDevelopment: 0,
      },
    });
    const effTarget = defaultScenario.energyEfficiencyTargetPercent ?? 0;
    setInterventionAreas({
      urbanInfill: 0,
      vmtReduction: defaultScenario.vmtReductionPercent,
      vehicleElectrification: 0,
      energyEfficiency: effTarget,
      buildingsElectrification: defaultScenario.heatPumpAdoptionPercent,
      gridDecarbonization: defaultScenario.gridCleanElectricityYear,
      organicsDiversion: defaultScenario.organicsDiversionPercent,
    });
    setSubPolicyValues({
      transitOrientedDevelopment: defaultScenario.vmtReductionComponents.transitOrientedDevelopment,
      downtownHomesPercent: defaultScenario.downtownHomesPercent ?? 70,
      activeTransportation: defaultScenario.vmtReductionComponents.activeTransportation,
      transitImprovements: defaultScenario.vmtReductionComponents.transitImprovements,
      parkingPricing: defaultScenario.parkingPricing.priceIncreasePercent,
      micromobility: defaultScenario.micromobility?.scalePercent || 0,
      otherInitiatives: 0,
    });
  };

  // Initialize from URL params once on first mount.
  useEffect(() => {
    if (urlInitializedRef.current) return;
    urlInitializedRef.current = true;
    if (typeof window !== 'undefined') {
      urlHadCompactDevelopmentParamRef.current = new URLSearchParams(window.location.search).has('compactDevelopment');
    }

    (async () => {
      try {
        const parsedUrlParams = parseScenarioFromUrl();
        const { params: urlParams, invalidKeys } = validateScenarioUrlParams(parsedUrlParams);

        // Priority 1: scenarioId from Supabase
        if (urlParams.scenarioId) {
          try {
            setScenarioId(urlParams.scenarioId);
            const saved = await loadScenarioById(urlParams.scenarioId);
            if (saved?.scenario_json) {
              const hydrated = hydrateToolFromScenarioState(saved.scenario_json);
              if (hydrated.emfacVersion) setEmfacVersion(hydrated.emfacVersion);
              if (hydrated.buildingsBauScenario) setBuildingsBauScenario(hydrated.buildingsBauScenario);
              if (hydrated.localScenarioName) handleLoadPredefinedScenario(hydrated.localScenarioName);
              setLocalScenarioName(hydrated.localScenarioName);
              if (hydrated.interventionAreas) {
                const rawEv = hydrated.interventionAreas.vehicleElectrification ?? 0;
                if (hydrated.evSliderSemantics !== 'beyondBau') {
                  pendingSavedAbsoluteEvRef.current = rawEv;
                }
                setInterventionAreas((prev) => ({
                  ...prev,
                  vehicleElectrification:
                    hydrated.evSliderSemantics === 'beyondBau' ? rawEv : prev.vehicleElectrification,
                  vmtReduction: hydrated.interventionAreas?.vmtReduction ?? prev.vmtReduction,
                  energyEfficiency: hydrated.interventionAreas?.energyEfficiency ?? prev.energyEfficiency,
                  buildingsElectrification:
                    hydrated.interventionAreas?.buildingsElectrification ?? prev.buildingsElectrification,
                  gridDecarbonization:
                    hydrated.interventionAreas?.gridDecarbonization ?? prev.gridDecarbonization,
                  organicsDiversion: hydrated.interventionAreas?.organicsDiversion ?? prev.organicsDiversion,
                }));
              }
              if (hydrated.subPolicyValues) {
                setSubPolicyValues((prev) => ({
                  ...prev,
                  transitOrientedDevelopment: hydrated.subPolicyValues?.compactDevelopment ?? prev.transitOrientedDevelopment,
                  downtownHomesPercent: hydrated.subPolicyValues?.downtownHomesPercent ?? prev.downtownHomesPercent,
                  activeTransportation: hydrated.subPolicyValues?.activeTransportation ?? prev.activeTransportation,
                  transitImprovements: hydrated.subPolicyValues?.transitImprovements ?? prev.transitImprovements,
                  parkingPricing: hydrated.subPolicyValues?.parkingPricing ?? prev.parkingPricing,
                  micromobility: hydrated.subPolicyValues?.micromobility ?? prev.micromobility,
                }));
                if (hydrated.subPolicyValues.compactDevelopment === 0) {
                  setCompactAllowBelowBauGrowth(true);
                }
              }
              if (hydrated.energyEfficiencyComponents) {
                setScenario((prev) => ({
                  ...prev,
                  energyEfficiencyComponents: normalizeEnergyEfficiencyComponents({
                    ...(prev.energyEfficiencyComponents ?? {}),
                    ...hydrated.energyEfficiencyComponents,
                  }),
                }));
              }
              savedScenarioLoadedFromUrlRef.current = true;
              return;
            }
          } catch (e) {
            // fall back to URL params
          }
        }

        // Priority 2: readable URL params
        if (urlParams.transport === 'emfac2025' || urlParams.transport === 'emfac2021') {
          setEmfacVersion(urlParams.transport === 'emfac2025' ? '2025' : '2021');
        }

        if (urlParams.buildings === 'conservative' || urlParams.buildings === 'accelerated' || urlParams.buildings === 'current_policy' || urlParams.buildings === 'bau') {
          const mapped = urlParams.buildings === 'bau' ? 'current_policy' : urlParams.buildings;
          setBuildingsBauScenario(mapped);
        }

        if (urlParams.local) {
          handleLoadPredefinedScenario(urlParams.local);
        }

        const resolution = applyScenarioPresetAndOverrides(urlParams);
        const applied = resolution.values;
        // `ev` with evMode=beyond (or preset-only beyond defaults) → UI beyond-BAU.
        // Legacy bookmarks: bare `ev` without evMode → absolute fleet EV%, migrate when BAU loads.
        if (applied.ev != null) {
          if (urlParams.ev != null && urlParams.evMode !== 'beyond') {
            pendingLegacyAbsoluteEvRef.current = Math.max(0, Math.min(100, applied.ev));
          } else {
            setInterventionAreas((prev) => ({
              ...prev,
              vehicleElectrification: Math.max(0, Math.min(100, applied.ev!)),
            }));
          }
        }
        setInterventionAreas((prev) => ({
          ...prev,
          vmtReduction:
            applied.vmt != null ? Math.max(0, Math.min(20, applied.vmt)) : prev.vmtReduction,
          gridDecarbonization:
            applied.elecYear != null ? Math.max(2025, Math.min(2045, Math.round(applied.elecYear))) : prev.gridDecarbonization,
          energyEfficiency:
            urlParams.energyEfficiency != null ? Math.max(0, Math.min(40, urlParams.energyEfficiency)) : prev.energyEfficiency,
          buildingsElectrification:
            urlParams.buildingsElectrification != null
              ? Math.max(0, Math.min(100, urlParams.buildingsElectrification))
              : prev.buildingsElectrification,
          organicsDiversion:
            urlParams.organics != null ? Math.max(0, Math.min(50, urlParams.organics)) : prev.organicsDiversion,
        }));
        setSubPolicyValues((prev) => ({
          ...prev,
          transitOrientedDevelopment:
            urlParams.compactDevelopment != null
              ? Math.max(0, Math.min(3, urlParams.compactDevelopment))
              : prev.transitOrientedDevelopment,
          downtownHomesPercent:
            urlParams.downtownHomes != null ? Math.max(0, Math.min(100, urlParams.downtownHomes)) : prev.downtownHomesPercent,
          activeTransportation:
            urlParams.activeTransportation != null
              ? Math.max(0, Math.min(10, urlParams.activeTransportation))
              : prev.activeTransportation,
          transitImprovements:
            urlParams.transitImprovements != null
              ? Math.max(0, Math.min(50, urlParams.transitImprovements))
              : prev.transitImprovements,
          parkingPricing:
            urlParams.parkingPricing != null ? Math.max(0, Math.min(100, urlParams.parkingPricing)) : prev.parkingPricing,
          micromobility:
            urlParams.micromobility != null ? Math.max(0, Math.min(200, urlParams.micromobility)) : prev.micromobility,
        }));
        if (urlParams.compactDevelopment === 0) {
          setCompactAllowBelowBauGrowth(true);
        }
        setScenario((prev) => ({
          ...prev,
          energyEfficiencyComponents: normalizeEnergyEfficiencyComponents({
            ...(prev.energyEfficiencyComponents ?? {}),
            residentialRetrofitExistingPercent:
              urlParams.residentialRetrofit != null
                ? Math.max(0, Math.min(50, urlParams.residentialRetrofit))
                : prev.energyEfficiencyComponents?.residentialRetrofitExistingPercent,
            commercialRetrofitExistingPercent:
              urlParams.commercialRetrofit != null
                ? Math.max(0, Math.min(50, urlParams.commercialRetrofit))
                : prev.energyEfficiencyComponents?.commercialRetrofitExistingPercent,
            residentialReachCodePercent:
              urlParams.residentialReachCode != null
                ? Math.max(0, Math.min(40, urlParams.residentialReachCode))
                : prev.energyEfficiencyComponents?.residentialReachCodePercent,
            otherEfficiencyPercent:
              urlParams.otherEfficiency != null
                ? Math.max(0, Math.min(40, urlParams.otherEfficiency))
                : prev.energyEfficiencyComponents?.otherEfficiencyPercent,
          }),
        }));
      } finally {
        setUrlScenarioHydrated(true);
      }
    })();
  // Intentionally run once; values are read from URL at mount time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL params in sync with key scenario controls.
  useEffect(() => {
    if (!urlScenarioHydrated) return;
    // Don't rewrite `ev` until legacy absolute bookmarks have been converted.
    if (pendingLegacyAbsoluteEvRef.current != null || pendingSavedAbsoluteEvRef.current != null) return;
    updateScenarioUrl({
      transport: emfacVersion === '2025' ? 'emfac2025' : 'emfac2021',
      buildings: buildingsBauScenario === 'current_policy' ? 'bau' : buildingsBauScenario,
      local: localScenarioName as LocalPresetParam | undefined,
      ev: Math.round(interventionAreas.vehicleElectrification * 10) / 10,
      evMode: 'beyond',
      vmt: Math.round(interventionAreas.vmtReduction * 10) / 10,
      elecYear: Math.round(interventionAreas.gridDecarbonization),
      energyEfficiency: Math.round((interventionAreas.energyEfficiency ?? 0) * 10) / 10,
      buildingsElectrification: Math.round((interventionAreas.buildingsElectrification ?? 0) * 10) / 10,
      organics: Math.round(interventionAreas.organicsDiversion ?? 0),
      compactDevelopment: Math.round((subPolicyValues.transitOrientedDevelopment ?? 0) * 10) / 10,
      downtownHomes: Math.round(subPolicyValues.downtownHomesPercent ?? 70),
      activeTransportation: Math.round((subPolicyValues.activeTransportation ?? 0) * 10) / 10,
      transitImprovements: Math.round((subPolicyValues.transitImprovements ?? 0) * 10) / 10,
      parkingPricing: Math.round((subPolicyValues.parkingPricing ?? 0) * 10) / 10,
      micromobility: Math.round((subPolicyValues.micromobility ?? 0) * 10) / 10,
      residentialRetrofit: Math.round((scenario.energyEfficiencyComponents?.residentialRetrofitExistingPercent ?? 0) * 10) / 10,
      commercialRetrofit: Math.round((scenario.energyEfficiencyComponents?.commercialRetrofitExistingPercent ?? 0) * 10) / 10,
      residentialReachCode: Math.round((scenario.energyEfficiencyComponents?.residentialReachCodePercent ?? 0) * 10) / 10,
      otherEfficiency: Math.round((scenario.energyEfficiencyComponents?.otherEfficiencyPercent ?? 0) * 10) / 10,
      scenarioId,
    });
  }, [
    urlScenarioHydrated,
    results?.baseline.currentEVPercent,
    emfacVersion,
    buildingsBauScenario,
    localScenarioName,
    scenarioId,
    subPolicyValues.transitOrientedDevelopment,
    subPolicyValues.downtownHomesPercent,
    subPolicyValues.activeTransportation,
    subPolicyValues.transitImprovements,
    subPolicyValues.parkingPricing,
    subPolicyValues.micromobility,
    scenario.energyEfficiencyComponents?.residentialRetrofitExistingPercent,
    scenario.energyEfficiencyComponents?.commercialRetrofitExistingPercent,
    scenario.energyEfficiencyComponents?.residentialReachCodePercent,
    scenario.energyEfficiencyComponents?.otherEfficiencyPercent,
    interventionAreas.vehicleElectrification,
    interventionAreas.vmtReduction,
    interventionAreas.energyEfficiency,
    interventionAreas.buildingsElectrification,
    interventionAreas.organicsDiversion,
    interventionAreas.gridDecarbonization,
  ]);

  const handleSaveScenario = React.useCallback(async () => {
    if (!user?.id) {
      return;
    }
    try {
      setSavingScenario(true);
      const state = buildScenarioStateFromTool({
        location,
        emfacVersion,
        buildingsBauScenario,
        localScenarioName,
        interventionAreas: {
          vehicleElectrification: interventionAreas.vehicleElectrification,
          vmtReduction: interventionAreas.vmtReduction,
          energyEfficiency: interventionAreas.energyEfficiency,
          buildingsElectrification: interventionAreas.buildingsElectrification,
          gridDecarbonization: interventionAreas.gridDecarbonization,
          organicsDiversion: interventionAreas.organicsDiversion,
        },
        subPolicyValues: {
          compactDevelopment: subPolicyValues.transitOrientedDevelopment,
          downtownHomesPercent: subPolicyValues.downtownHomesPercent,
          activeTransportation: subPolicyValues.activeTransportation,
          transitImprovements: subPolicyValues.transitImprovements,
          parkingPricing: subPolicyValues.parkingPricing,
          micromobility: subPolicyValues.micromobility,
        },
        energyEfficiencyComponents: {
          residentialRetrofitExistingPercent: scenario.energyEfficiencyComponents?.residentialRetrofitExistingPercent,
          commercialRetrofitExistingPercent: scenario.energyEfficiencyComponents?.commercialRetrofitExistingPercent,
          residentialReachCodePercent: scenario.energyEfficiencyComponents?.residentialReachCodePercent,
          otherEfficiencyPercent: scenario.energyEfficiencyComponents?.otherEfficiencyPercent,
        },
      });
      const scenarioJson = serializeScenarioState(state);
      const saved = await saveScenario({
        name: null,
        description: null,
        location,
        transport_scenario: state.emfacVersion === '2025' ? 'emfac2025' : 'emfac2021',
        buildings_scenario: state.buildingsBauScenario === 'current_policy' ? 'bau' : state.buildingsBauScenario,
        local_preset: state.localScenarioName ?? null,
        scenario_json: scenarioJson,
        cost_assumptions: null,
        results_json: null,
        model_version: 'policy-tool-v2',
        is_public: false,
        created_by: user.id,
      });
      setScenarioId(saved.id);
    } catch (e) {
      console.error('Failed to save scenario', e);
    } finally {
      setSavingScenario(false);
    }
  }, [
    location,
    emfacVersion,
    buildingsBauScenario,
    localScenarioName,
    interventionAreas.vehicleElectrification,
    interventionAreas.vmtReduction,
    interventionAreas.energyEfficiency,
    interventionAreas.buildingsElectrification,
    interventionAreas.organicsDiversion,
    interventionAreas.gridDecarbonization,
    subPolicyValues.transitOrientedDevelopment,
    subPolicyValues.downtownHomesPercent,
    subPolicyValues.activeTransportation,
    subPolicyValues.transitImprovements,
    subPolicyValues.parkingPricing,
    subPolicyValues.micromobility,
    scenario.energyEfficiencyComponents?.residentialRetrofitExistingPercent,
    scenario.energyEfficiencyComponents?.commercialRetrofitExistingPercent,
    scenario.energyEfficiencyComponents?.residentialReachCodePercent,
    scenario.energyEfficiencyComponents?.otherEfficiencyPercent,
    user?.id,
  ]);

  // Energy efficiency sub-policy values from scenario (policy mode when expanded)
  const energyEfficiencySubPolicyValues: EnergyEfficiencySubPolicyValues = (() => {
    const c = normalizeEnergyEfficiencyComponents(scenario.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents());
    return {
      residentialReachCodePercent: c.residentialReachCodePercent ?? 0,
      residentialRetrofitExistingPercent: c.residentialRetrofitExistingPercent ?? 0,
      commercialRetrofitExistingPercent: c.commercialRetrofitExistingPercent ?? 0,
      otherEfficiencyPercent: c.otherEfficiencyPercent ?? 0,
    };
  })();

  const energyEfficiencyOtherResidualDisplay = useMemo(() => {
    if (expandedIntervention !== 'energyEfficiency') return undefined;
    const bauT = results?.baseline?.bauTherms2045 ?? 1;
    const bauK = results?.baseline?.bauKwh2045 ?? 1;
    const c = scenario.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents();
    const fNamed = computeTotalEfficiencyPercentFromComponents(
      { ...c, otherEfficiencyPercent: 0 },
      bauT,
      bauK,
      efficiencyLayerOptions
    );
    const headline = interventionAreas.energyEfficiency ?? 0;
    return Math.max(0, headline - fNamed);
  }, [
    expandedIntervention,
    scenario.energyEfficiencyComponents,
    interventionAreas.energyEfficiency,
    results?.baseline?.bauTherms2045,
    results?.baseline?.bauKwh2045,
    efficiencyLayerOptions,
  ]);

  const handleEnergyEfficiencySubPolicyChange = React.useCallback(
    (values: EnergyEfficiencySubPolicyValues) => {
      const components = normalizeEnergyEfficiencyComponents(values);
      const bauT = results?.baseline?.bauTherms2045 ?? 1;
      const bauK = results?.baseline?.bauKwh2045 ?? 1;
      const total = computeTotalEfficiencyPercentFromComponents(components, bauT, bauK, efficiencyLayerOptions);
      setScenario(prev => ({
        ...prev,
        energyEfficiencyComponents: components,
        energyEfficiencyTargetPercent: total,
        energyEfficiencyPercent: total,
      }));
      setInterventionAreas(prev => ({ ...prev, energyEfficiency: total }));
    },
    [
      results?.baseline?.bauTherms2045,
      results?.baseline?.bauKwh2045,
      efficiencyLayerOptions,
    ]
  );

  const decisionSummary = useMemo(() => {
    if (!results) return null;

    const fmtPct = (value: number, total: number): number => (total > 0 ? (value / total) * 100 : 0);
    const formatRate = (value: number): string => `$${value.toFixed(3)}`;

    const buildingsUpfront = buildingsCostResults?.costEffectivenessMetrics?.upfrontCapitalCost ?? 0;
    const transportUpfront = transportCostResults?.upfrontCapitalCost ?? 0;
    const totalCapitalInvestment = buildingsUpfront + transportUpfront;

    const buildingsNpv = buildingsCostResults?.costEffectivenessMetrics?.netPresentValue ?? 0;
    const transportNpv = transportCostResults?.netPresentValue ?? 0;
    const netSavingsNpv = buildingsNpv + transportNpv;

    const buildingsCumulative = buildingsCostResults?.costEffectivenessMetrics?.cumulativeNetCost ?? 0;
    const transportCumulative = transportCostResults?.cumulativeNetCost ?? 0;
    const totalCumulativeNetCost = buildingsCumulative + transportCumulative;

    const emissionsReductionMT =
      (buildingsCostResults?.totalBuildingEmissionsReductionMT ?? 0) + (transportCostResults?.emissionsReductionMT ?? 0);
    const costPerTon = emissionsReductionMT > 0 ? totalCumulativeNetCost / emissionsReductionMT : null;
    const buildingAnnualOperatingSavings2045 =
      buildingsCostResults?.annualSavingsSeries?.[buildingsCostResults.annualSavingsSeries.length - 1]?.value ?? 0;
    const transportAnnualOperatingSavings2045 =
      transportCostResults?.annualFuelSavingsSeries?.[transportCostResults.annualFuelSavingsSeries.length - 1]?.value ?? 0;
    const annualOperatingSavings2045 = buildingAnnualOperatingSavings2045 + transportAnnualOperatingSavings2045;
    const cumulativeOperatingSavings =
      (buildingsCostResults?.annualSavingsSeries?.reduce((sum, row) => sum + row.value, 0) ?? 0) +
      (transportCostResults?.annualFuelSavingsSeries?.reduce((sum, row) => sum + row.value, 0) ?? 0);

    const sectorRows = [
      {
        key: 'transport',
        label: 'Transport',
        tons: (results.reductions.lightDutyReduction ?? 0) + (results.reductions.heavyDutyReduction ?? 0),
      },
      {
        key: 'buildings',
        label: 'Buildings',
        tons: (results.reductions.naturalGasReduction ?? 0) + (results.reductions.electricityReduction ?? 0),
      },
      {
        key: 'waste',
        label: 'Waste',
        tons: results.reductions.wasteReduction ?? 0,
      },
    ];
    const totalReduction = Math.max(0, results.reductions.totalReduction ?? 0);
    const sectors = sectorRows.map((r) => ({ ...r, percent: fmtPct(r.tons, totalReduction) }));

    const localPolicyRowsBase = [
      { key: 'evAdoption', label: 'EV adoption', tons: results.contributionsByPolicy.evAdoption ?? 0 },
      { key: 'vmtReduction', label: 'VMT reduction', tons: results.contributionsByPolicy.vmtReduction ?? 0 },
      { key: 'heatPumpAdoption', label: 'Building electrification', tons: results.contributionsByPolicy.heatPumpAdoption ?? 0 },
      { key: 'energyEfficiency', label: 'Energy efficiency', tons: results.contributionsByPolicy.energyEfficiency ?? 0 },
      { key: 'organicsDiversion', label: 'Organics diversion', tons: results.contributionsByPolicy.organicsDiversion ?? 0 },
    ];
    const localPolicyTotal = localPolicyRowsBase.reduce((sum, r) => sum + r.tons, 0);
    const localPolicyRows = localPolicyRowsBase.map((r) => ({ ...r, percent: fmtPct(r.tons, localPolicyTotal) }));

    const efficiencyContrib = efficiencyContributions2045;
    const efficiencyContribTotal =
      (efficiencyContrib?.residentialRetrofit ?? 0) +
      (efficiencyContrib?.commercialRetrofit ?? 0) +
      (efficiencyContrib?.residentialReach ?? 0) +
      (efficiencyContrib?.other ?? 0);
    const efficiencyEmissionAllocation = {
      residentialRetrofit:
        efficiencyContribTotal > 0
          ? (results.contributionsByPolicy.energyEfficiency ?? 0) *
            ((efficiencyContrib?.residentialRetrofit ?? 0) / efficiencyContribTotal)
          : 0,
      commercialRetrofit:
        efficiencyContribTotal > 0
          ? (results.contributionsByPolicy.energyEfficiency ?? 0) *
            ((efficiencyContrib?.commercialRetrofit ?? 0) / efficiencyContribTotal)
          : 0,
      residentialReach:
        efficiencyContribTotal > 0
          ? (results.contributionsByPolicy.energyEfficiency ?? 0) * ((efficiencyContrib?.residentialReach ?? 0) / efficiencyContribTotal)
          : 0,
      other:
        efficiencyContribTotal > 0
          ? (results.contributionsByPolicy.energyEfficiency ?? 0) * ((efficiencyContrib?.other ?? 0) / efficiencyContribTotal)
          : 0,
    };

    const topPolicyImpacts = [
      {
        key: 'compact',
        label: 'Compact development',
        tons: results.contributionsByPolicy.vmtReductionByComponent?.transitOrientedDevelopment ?? 0,
      },
      {
        key: 'resRetrofit',
        label: 'Existing residential retrofit',
        tons: efficiencyEmissionAllocation.residentialRetrofit,
      },
      {
        key: 'comRetrofit',
        label: 'Existing commercial retrofit',
        tons: efficiencyEmissionAllocation.commercialRetrofit,
      },
      { key: 'ev', label: 'EV adoption', tons: results.contributionsByPolicy.evAdoption ?? 0 },
      {
        key: 'activeTransportation',
        label: 'Active transportation',
        tons: results.contributionsByPolicy.vmtReductionByComponent?.activeTransportation ?? 0,
      },
      {
        key: 'transit',
        label: 'Transit improvements',
        tons: results.contributionsByPolicy.vmtReductionByComponent?.transitImprovements ?? 0,
      },
      {
        key: 'buildingElectrification',
        label: 'Building electrification',
        tons: results.contributionsByPolicy.heatPumpAdoption ?? 0,
      },
      {
        key: 'resReach',
        label: 'New residential reach code',
        tons: efficiencyEmissionAllocation.residentialReach,
      },
      {
        key: 'effOther',
        label: 'Other efficiency',
        tons: efficiencyEmissionAllocation.other,
      },
      {
        key: 'organics',
        label: 'Organics diversion',
        tons: results.contributionsByPolicy.organicsDiversion ?? 0,
      },
    ]
      .filter((r) => Number.isFinite(r.tons) && r.tons > 0)
      .sort((a, b) => b.tons - a.tons);

    const stateReductionTons = Math.max(0, totalReduction - localPolicyTotal);
    const localReductionTons = Math.max(0, localPolicyTotal);
    const stateReductionPercent = fmtPct(stateReductionTons, totalReduction);
    const localReductionPercent = fmtPct(localReductionTons, totalReduction);

    // Sector split for local-policy-driven reductions (aligns narrative with policy lever breakdown).
    const localSectorRows = [
      {
        key: 'transport',
        label: 'Transport',
        tons: (results.contributionsByPolicy.evAdoption ?? 0) + (results.contributionsByPolicy.vmtReduction ?? 0),
      },
      {
        key: 'buildings',
        label: 'Buildings',
        tons: (results.contributionsByPolicy.heatPumpAdoption ?? 0) + (results.contributionsByPolicy.energyEfficiency ?? 0),
      },
      {
        key: 'waste',
        label: 'Waste',
        tons: results.contributionsByPolicy.organicsDiversion ?? 0,
      },
    ];
    const localSectorTotal = localSectorRows.reduce((sum, row) => sum + row.tons, 0);
    const localSectors = localSectorRows.map((row) => ({ ...row, percent: fmtPct(row.tons, localSectorTotal) }));

    const stateNpv =
      (buildingsCostBau?.costEffectivenessMetrics?.netPresentValue ?? 0) + (transportCostBau?.netPresentValue ?? 0);
    const localNpv =
      (buildingsCostLocalIncremental?.costEffectivenessMetrics?.netPresentValue ?? 0) +
      (transportCostLocalIncremental?.netPresentValue ?? 0);

    const bSeries = buildingsCostResults?.cumulativeNetCostSeries ?? [];
    const tSeries = transportCostResults?.cumulativeNetCostSeries ?? [];
    const yearSet = new Set<number>();
    bSeries.forEach((r) => yearSet.add(r.year));
    tSeries.forEach((r) => yearSet.add(r.year));
    const years = Array.from(yearSet).sort((a, b) => a - b);
    const bByYear = new Map(bSeries.map((r) => [r.year, r.value]));
    const tByYear = new Map(tSeries.map((r) => [r.year, r.value]));

    let paybackYear: number | null = null;
    const hasInvestment = totalCapitalInvestment > 0;
    if (hasInvestment) {
      let seenPositive = false;
      for (const y of years) {
        const combined = (bByYear.get(y) ?? 0) + (tByYear.get(y) ?? 0);
        if (combined > 0) seenPositive = true;
        if (combined <= 0 && seenPositive) {
          paybackYear = y;
          break;
        }
      }
    }

    const dominantCostDriver = Math.abs(transportNpv) >= Math.abs(buildingsNpv) ? 'transport' : 'buildings';

    const headline = netSavingsNpv >= 0 ? 'Net savings' : 'Net cost';
    const headlineValue = currencyShort(Math.abs(netSavingsNpv));
    const costPerTonLabel =
      costPerTon == null ? 'Not available' : costPerTon <= 0 ? 'Cost-saving' : 'Cost-incurring';
    const paybackInterpretation =
      paybackYear == null
        ? 'No modeled payback by 2045.'
        : `Modeled payback occurs in ${paybackYear}.`;

    const driverExplanation =
      dominantCostDriver === 'transport'
        ? `Transport is the dominant financial driver (${currencyShort(transportNpv)} NPV vs ${currencyShort(buildingsNpv)} for buildings).`
        : `Buildings are the dominant financial driver (${currencyShort(buildingsNpv)} NPV vs ${currencyShort(transportNpv)} for transport).`;

    const capexVsSavingsExplanation =
      netSavingsNpv >= 0
        ? `Operating savings outweigh total capital investment (${currencyShort(totalCapitalInvestment)}), resulting in a positive NPV.`
        : `Total capital investment (${currencyShort(totalCapitalInvestment)}) outweighs operating savings over the analysis period, resulting in a net cost.`;

    const dominantSector = localSectors.slice().sort((a, b) => b.tons - a.tons)[0]?.label ?? 'Transport';
    const transportDominanceExplanation =
      dominantSector === 'Transport'
        ? 'Transport is the top local policy reduction sector because EV adoption and VMT levers contribute the largest share of local policy-driven reductions.'
        : 'Transport is not the top local policy reduction sector in this scenario; buildings or waste levers are currently contributing more of local policy-driven reductions.';

    const buildingsCostDynamicsExplanation =
      buildingsNpv < 0
        ? 'Buildings show net cost because upfront retrofits/electrification investments are large relative to modeled operating savings over the period.'
        : 'Buildings show net savings because operating bill reductions and avoided fuel costs are large enough to offset upfront investments.';

    const recommendationNarrative = [
      `For ${location}, prioritize the highest-impact levers first: ${topPolicyImpacts.slice(0, 3).map((p) => p.label.toLowerCase()).join(', ') || 'core transport and buildings measures'}. Pair these with implementation sequencing that captures early operating savings while pacing capital deployment.`,
      `Use transportation demand and electrification policies to secure near-term tonnage reductions, then scale building efficiency/electrification where co-benefits are strongest. Track progress using sector-level reductions and cost-per-ton, and revisit BAU assumptions (EMFAC, buildings pathway, grid clean year, and waste diversion) annually to keep strategy aligned with observed trends.`,
    ];
    const electricityPriceUsed = policyCostAssumptions.electricityPricePerKwh;
    const gasPricePerTherm = policyCostAssumptions.naturalGasPricePerTherm;
    const kwhPerThermEquivalent = 29.3 / Math.max(0.1, buildingsCop);
    const gasEquivalentPricePerKwh = gasPricePerTherm / kwhPerThermEquivalent;
    const breakEvenElectricityPrice = gasPricePerTherm / (29.3 / Math.max(0.1, buildingsCop));
    const billImpactDetail: BuildingBillImpactDetail | null =
      buildingsCostResults?.buildingBillImpactDetail ?? null;
    const annualBillImpact =
      billImpactDetail?.citywideTotalAnnualBillImpact ??
      (buildingsCostResults?.debug?.year2045?.policyBill ?? 0) -
        (buildingsCostResults?.debug?.year2045?.bauBill ?? 0);
    const monthlyBillImpact = annualBillImpact / 12;
    const cumulativeBillImpact =
      (buildingsCostResults?.policyBillByYear?.reduce((sum, row) => sum + row.value, 0) ?? 0) -
      (buildingsCostResults?.bauBillByYear?.reduce((sum, row) => sum + row.value, 0) ?? 0);
    const electrificationOperatingCostNarrative =
      electricityPriceUsed > breakEvenElectricityPrice
        ? `Under current prices, building electrification reduces emissions but can increase operating bills because electricity (${formatRate(electricityPriceUsed)}/kWh) is above the break-even price (${formatRate(breakEvenElectricityPrice)}/kWh at COP ${buildingsCop.toFixed(1)}).`
        : `Under current prices, building electrification reduces emissions and tends to reduce operating bills because electricity (${formatRate(electricityPriceUsed)}/kWh) is below the break-even price (${formatRate(breakEvenElectricityPrice)}/kWh at COP ${buildingsCop.toFixed(1)}).`;
    const capexVsOpexNarrative =
      'Interpretation note: capital cost is upfront investment in equipment/building upgrades, while operating cost is annual utility/fuel spending. Both drive total NPV.';
    const rateStructureNarrative =
      'Policy insight: utility rate structure (electricity and gas prices, fixed charges, and time-of-use design) strongly affects building electrification economics and payback.';

    return {
      netSavingsNpv,
      costPerTon,
      paybackYear,
      totalCapitalInvestment,
      dominantCostDriver,
      headline,
      headlineValue,
      costPerTonLabel,
      paybackInterpretation,
      driverExplanation,
      capexVsSavingsExplanation,
      sectors,
      localSectors,
      localPolicyRows,
      topPolicyImpacts,
      stateLocal: {
        stateReductionTons,
        stateReductionPercent,
        localReductionTons,
        localReductionPercent,
        stateNpv,
        localNpv,
      },
      narratives: {
        transportDominanceExplanation,
        buildingsCostDynamicsExplanation,
        electrificationOperatingCostNarrative,
        capexVsOpexNarrative,
        rateStructureNarrative,
        recommendationNarrative,
      },
      annualEnergyBillImpact: {
        annualOperatingSavings2045,
        cumulativeOperatingSavings,
      },
      buildingElectrificationBillImpact: {
        /** Citywide total annual bill impact (2045): residential + commercial modeled buildings. */
        citywideTotalAnnualBillImpact: annualBillImpact,
        monthlyCitywideTotalBillImpact: monthlyBillImpact,
        cumulativeBillImpact,
        billImpactDetail,
        /** @deprecated Use citywideTotalAnnualBillImpact — same value. */
        annualBillImpact,
        /** @deprecated Use monthlyCitywideTotalBillImpact — same value. */
        monthlyBillImpact,
      },
      energyPriceDiagnostics: {
        electricityPricePerKwh: electricityPriceUsed,
        gasPricePerTherm,
        gasEquivalentPricePerKwh,
        kwhPerThermEquivalent,
        priceSource: electricityPriceContext.source,
      },
      breakEvenElectricityPrice,
      actualElectricityPrice: electricityPriceUsed,
      bauAssumptions: {
        transport: `EMFAC${emfacVersion} baseline`,
        buildings: `${buildingsBauScenario} pathway, RNG ${Math.round(buildingsRngPercent)}%, COP ${buildingsCop.toFixed(1)}`,
        electricity: `Grid clean electricity year ${Math.round((scenario as { gridCleanElectricityYear?: number }).gridCleanElectricityYear ?? 2045)}`,
        waste: `Additional organics diversion target ${Math.round((scenario as { organicsDiversionPercent?: number }).organicsDiversionPercent ?? 0)}% (beyond BAU)`,
      },
    };
  }, [
    results,
    location,
    scenario,
    emfacVersion,
    buildingsBauScenario,
    buildingsRngPercent,
    buildingsCop,
    buildingsCostResults,
    transportCostResults,
    buildingsCostBau,
    transportCostBau,
    buildingsCostLocalIncremental,
    transportCostLocalIncremental,
    efficiencyContributions2045,
    electricityPriceContext,
    policyCostAssumptions,
  ]);

  // Expose current policy scenario context for structured PDF/Word export path.
  useEffect(() => {
    if (typeof window === 'undefined' || !results) return;
    const payload: ScenarioReportInput = {
      scenario,
      results,
      costResults: {
        buildings: buildingsCostResults,
        transport: transportCostResults,
      },
      breakdowns: {
        stateReduction: decisionSummary?.stateLocal.stateReductionTons,
        localReduction: decisionSummary?.stateLocal.localReductionTons,
        stateCost: decisionSummary?.stateLocal.stateNpv,
        localCost: decisionSummary?.stateLocal.localNpv,
      },
    };
    (window as Window & { __CLIMATEPLANS_SCENARIO_EXPORT_CONTEXT__?: ScenarioReportInput }).__CLIMATEPLANS_SCENARIO_EXPORT_CONTEXT__ = payload;
  }, [scenario, results, buildingsCostResults, transportCostResults, decisionSummary]);

  return (
    <div className="policy-impact-tool">
      {error && (
        <div className="policy-tool-error">
          <p>{error}</p>
        </div>
      )}

      <div className="policy-tool-content">
        {/* GHG Forecast: clean layout with chart left, metrics right */}
        <section className="policy-main-forecast">
          <div className="policy-main-forecast-header">
            <h2 className="section-title">{compareMode ? 'GHG Forecast' : `GHG Forecast - ${location}`}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <label htmlFor="emfac-version-select" style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  EMFAC Version:
                </label>
                <select
                  id="emfac-version-select"
                  value={emfacVersion}
                  onChange={(e) => {
                    // Keep baseline + chart caches warm (keys include EMFAC version).
                    setMicromobilityVMTReductionPercent(0);
                    setTransitVMTReductionPercent(0);
                    setEmfacVersion(e.target.value as '2021' | '2025');
                  }}
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                    backgroundColor: 'var(--chart-container-bg)',
                    cursor: 'pointer',
                    fontWeight: 500,
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="2025">EMFAC2025</option>
                  <option value="2021">EMFAC2021</option>
                </select>
              </div>
              {!compareMode && (
              <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <label htmlFor="buildings-bau-select" style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Buildings BAU:
                </label>
                <select
                  id="buildings-bau-select"
                  value={buildingsBauScenario}
                  onChange={(e) => {
                    // Keep baseline + chart caches warm (keys include buildings BAU).
                    setBuildingsBauScenario(e.target.value as 'conservative' | 'current_policy' | 'accelerated');
                  }}
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                    backgroundColor: 'var(--chart-container-bg)',
                    cursor: 'pointer',
                    fontWeight: 500,
                    color: 'var(--text-primary)'
                  }}
                  title="Buildings BAU electrification pathway (stock turnover, sales curves). Accelerated assumes local governments encourage adoption 25% faster. RNG share is set under Building Electrification → Critical Assumptions."
                >
                  <option value="conservative">Conservative</option>
                  <option value="current_policy">Current policy</option>
                  <option value="accelerated">Accelerated (+25% local adoption)</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={handleSaveScenario}
                  disabled={savingScenario || !user}
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: 6,
                    fontSize: '0.8125rem',
                    backgroundColor: 'var(--chart-container-bg)',
                    cursor: savingScenario ? 'wait' : 'pointer',
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                  }}
                  title={
                    user
                      ? 'Saves this scenario to your account in the database and adds scenarioId to the URL for your own reload.'
                      : 'Sign in to save scenarios to your account.'
                  }
                >
                  {savingScenario ? 'Saving…' : 'Save Scenario'}
                </button>
              </div>
              </>
              )}
              {activeChart === 'stacked' && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}
                title="Reference trajectory on the stacked chart (dashed line). Does not change modeled emissions."
              >
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Target:
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  <input
                    type="number"
                    aria-label="Percent reduction below reference year"
                    min={0}
                    max={100}
                    step={1}
                    value={targetReductionPercent}
                    onChange={(e) => {
                      const { value } = e.target;
                      if (value === '') return;
                      const n = Number(value);
                      if (!Number.isFinite(n)) return;
                      setTargetReductionPercent(clampInt(n, 0, 100));
                    }}
                    onBlur={() =>
                      setTargetReductionPercent((p) => clampInt(p, 0, 100))
                    }
                    style={{
                      width: '3.5rem',
                      padding: '0.35rem 0.45rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: 4,
                      fontSize: '0.8125rem',
                      backgroundColor: 'var(--chart-container-bg)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>%</span>
                </span>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>below</span>
                <input
                  type="number"
                  aria-label="Reference inventory year for target"
                  min={TARGET_REF_YEAR_MIN}
                  max={TARGET_REF_YEAR_MAX}
                  step={1}
                  title={capReferenceYearSubstitutionHint}
                  value={targetBaselineYear}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value === '') return;
                    const n = Number(value);
                    if (!Number.isFinite(n)) return;
                    setTargetBaselineYear(clampInt(n, TARGET_REF_YEAR_MIN, TARGET_REF_YEAR_MAX));
                  }}
                  onBlur={() =>
                    setTargetBaselineYear((y) => clampInt(y, TARGET_REF_YEAR_MIN, TARGET_REF_YEAR_MAX))
                  }
                  style={{
                    width: '4.25rem',
                    padding: '0.35rem 0.45rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: 4,
                    fontSize: '0.8125rem',
                    backgroundColor: 'var(--chart-container-bg)',
                    color: 'var(--text-primary)',
                  }}
                />
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>levels by</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <input
                    type="number"
                    aria-label="Target year for linear reduction path"
                    min={TARGET_END_YEAR_MIN}
                    max={TARGET_END_YEAR_MAX}
                    step={1}
                    value={targetEndYear}
                    onChange={(e) => {
                      const { value } = e.target;
                      if (value === '') return;
                      const n = Number(value);
                      if (!Number.isFinite(n)) return;
                      setTargetEndYear(clampInt(n, TARGET_END_YEAR_MIN, TARGET_END_YEAR_MAX));
                    }}
                    onBlur={() =>
                      setTargetEndYear((y) => clampInt(y, TARGET_END_YEAR_MIN, TARGET_END_YEAR_MAX))
                    }
                    style={{
                      width: '4.25rem',
                      padding: '0.35rem 0.45rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: 4,
                      fontSize: '0.8125rem',
                      backgroundColor: 'var(--chart-container-bg)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  {capTargetDefaultsBanner ? (
                    <span className="policy-cap-target-info-wrap">
                      <button
                        type="button"
                        className="policy-cap-target-info-bubble"
                        aria-label="Climate Action Plan default target line"
                        aria-describedby={capTargetDefaultsInfoId}
                      >
                        ℹ
                      </button>
                      <span id={capTargetDefaultsInfoId} className="policy-cap-target-info-tooltip" role="tooltip">
                        {capTargetDefaultsBanner}
                      </span>
                    </span>
                  ) : null}
                </span>
              </div>
              )}
              <div className="chart-type-buttons">
              <button
                className={`chart-type-button ${activeChart === 'stacked' ? 'active' : ''}`}
                onClick={() => setActiveChart('stacked')}
              >
                Stacked
              </button>
              <button
                className={`chart-type-button ${activeChart === 'line' ? 'active' : ''}`}
                onClick={() => setActiveChart('line')}
              >
                Line Chart
              </button>
              <button
                className={`chart-type-button ${activeChart === 'contributions' ? 'active' : ''}`}
                onClick={() => setActiveChart('contributions')}
              >
                Policies
              </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <label className="compare-toggle" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.8125rem', color: '#374151', fontWeight: 500, userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={compareMode}
                    onChange={(e) => handleCompareToggle(e.target.checked)}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#3b82f6' }}
                  />
                  Compare Policies
                </label>
                <label className="percapita-toggle" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.8125rem', color: '#374151', fontWeight: 500, userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={perCapitaMode}
                    onChange={(e) => setPerCapitaMode(e.target.checked)}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#3b82f6' }}
                  />
                  Per capita
                </label>
              </div>
            </div>
          </div>

          {compareMode ? (
            <div className="policy-compare-container">
              <div className="policy-compare-panel">
                <div className="policy-compare-panel-header">
                  <span className="policy-compare-panel-title">Scenario A location</span>
                  <LocationSelector
                    onLocationSelect={(loc) => handlePanelLocationChange('A', loc)}
                    placeholder={panelALocation || location}
                    className="policy-compare-location-selector"
                  />
                </div>
                <PolicyPanel
                  key="panelA"
                  location={panelALocation || location}
                  geofips={panelAGeofips ?? geofips}
                  emfacVersion={emfacVersion}
                  activeChart={activeChart}
                  targetReductionPercent={targetReductionPercent}
                  targetBaselineYear={targetBaselineYear}
                  panelLabel="Scenario A"
                  perCapitaMode={perCapitaMode}
                  compact
                />
              </div>
              <div className="policy-compare-panel">
                <div className="policy-compare-panel-header">
                  <span className="policy-compare-panel-title">Scenario B location</span>
                  <LocationSelector
                    onLocationSelect={(loc) => handlePanelLocationChange('B', loc)}
                    placeholder={panelBLocation || location}
                    className="policy-compare-location-selector"
                  />
                </div>
                <PolicyPanel
                  key="panelB"
                  location={panelBLocation || location}
                  geofips={panelBGeofips ?? geofips}
                  emfacVersion={emfacVersion}
                  activeChart={activeChart}
                  targetReductionPercent={targetReductionPercent}
                  targetBaselineYear={targetBaselineYear}
                  panelLabel="Scenario B"
                  perCapitaMode={perCapitaMode}
                  compact
                />
              </div>
            </div>
          ) : (
          <>
          <div className="policy-main-forecast-layout">
            {results && (
              <div className="policy-main-metrics">
                <div className="impact-metric-card baseline">
                  <div className="metric-label">Current Baseline</div>
                  <div className="metric-value">
                    <span className="metric-number">
                      {(stackedTotals?.baselineTotal ?? results.baseline.totalGHG).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="metric-unit">MT CO₂e</span>
                  </div>
                </div>

                <div className="impact-metric-card projected">
                  <div className="metric-label">Projected with Policies</div>
                  <div className="metric-value">
                    <span className="metric-number">
                      {(stackedTotals?.projectedWithPoliciesTotal ?? results.projected.totalGHG).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="metric-unit">MT CO₂e</span>
                  </div>
                </div>

                <div className="impact-metric-card reduction">
                  <div className="metric-label">Total Reduction</div>
                  <div className="metric-value reduction-value">
                    <span className="metric-number">
                      {(stackedTotals?.totalReduction ?? results.reductions.totalReduction).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="metric-unit">MT CO₂e</span>
                  </div>
                </div>

                <div className="impact-metric-card percentage">
                  <div className="metric-label">Percent Reduction</div>
                  <div className="metric-value reduction-value">
                    <span className="metric-number">
                      {(stackedTotals?.percentReduction ?? results.reductions.percentReduction).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="policy-main-chart">
              <PolicyImpactChart
                results={results}
                loading={loading}
                loadProgress={loadProgress}
                scenario={scenario}
                location={location}
                chartType={activeChart}
                targetReductionPercent={targetReductionPercent}
                targetBaselineYear={targetBaselineYear}
                targetEndYear={targetEndYear}
                emfacVersion={emfacVersion}
                buildingsBauScenario={buildingsBauScenario}
                buildingsRngPercent={buildingsRngPercent}
                buildingsCop={buildingsCop}
                efficiencyLayerOptions={efficiencyLayerOptions}
                perCapitaMode={perCapitaMode}
                onTotalsChange={setStackedTotals}
                onTimeSeriesDataChange={handleTimeSeriesDataChange}
              />
            </div>
          </div>
          {/* Implementation by 2045 — policy levers and detailed policies (when selected) before financial analysis */}
          <div className="policy-intervention-areas-section">
              <InterventionAreasControl
                interventionAreas={interventionAreas}
                onChange={setInterventionAreas}
                expandedIntervention={expandedIntervention}
                onExpandedChange={setExpandedIntervention}
                onSubPolicyExpand={setExpandedSubPolicy}
                subPolicyValues={subPolicyValues}
                onSubPolicyChange={setSubPolicyValues}
                energyEfficiencySubPolicyValues={energyEfficiencySubPolicyValues}
                onEnergyEfficiencySubPolicyChange={handleEnergyEfficiencySubPolicyChange}
                energyEfficiencyOtherResidualDisplay={energyEfficiencyOtherResidualDisplay}
                parkingVMTReductionPercent={parkingVMTReductionPercent}
                computeParkingVMTReductionPercent={(priceIncreasePercent: number) =>
                  computeParkingVMTReductionPercent({
                    ...scenario.parkingPricing,
                    priceIncreasePercent,
                  })
                }
                micromobilityVMTReductionPercent={micromobilityVMTReductionPercent}
                transitVMTReductionPercent={transitVMTReductionPercent}
                compactDevelopmentMinPercent={compactDevelopmentSliderMinPercent}
                compactDevelopmentVmtReductionPercent={compactVmtReductionPercent}
                vehicleElectrificationBau2045Percent={results?.baseline.currentEVPercent}
                onLoadScenario={handleLoadPredefinedScenario}
                onResetScenario={handleResetScenario}
                activeScenarioName={localScenarioName ?? null}
                onClearScenarioName={() => setLocalScenarioName(undefined)}
              />
            </div>

          {/* Expanded Intervention Details */}
          {canExpandDetails && expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'parkingPricing' && (
            <div id="parking-pricing-details" className="policy-intervention-details-section">
              <h3 className="intervention-details-title">Parking Pricing Policy</h3>
              <div className="intervention-details-content">
                <ParkingPricingPolicy
                  assumptions={{
                    ...scenario.parkingPricing,
                    priceIncreasePercent: subPolicyValues.parkingPricing,
                  }}
                  onChange={(updatedAssumptions) => {
                    // Update scenario assumptions
                    setScenario(prev => ({
                      ...prev,
                      parkingPricing: updatedAssumptions,
                    }));

                    // Keep sub-policy slider in sync
                    setSubPolicyValues(prev => ({
                      ...prev,
                      parkingPricing: updatedAssumptions.priceIncreasePercent,
                    }));

                    // Update the top-level VMT Reduction slider (excludes Compact Development; model net statewide drives its impact)
                    const computedParking = computeParkingVMTReductionPercent(updatedAssumptions);
                    const sumOfPolicyReductions =
                      (subPolicyValues.activeTransportation || 0) +
                      transitVMTReductionPercent +
                      computedParking +
                      micromobilityVMTReductionPercent +
                      (compactVmtReductionPercent || 0);
                    setInterventionAreas(prev => ({
                      ...prev,
                      vmtReduction: Math.max(0, Math.min(20, sumOfPolicyReductions + (subPolicyValues.otherInitiatives || 0))),
                    }));
                  }}
                  currentVMT={results?.baseline.vmt || 0}
                  projectedBAUVMT={results?.baseline.vmt || 0}
                  location={location}
                  geofips={geofips}
                />
              </div>
            </div>
          )}

          {canExpandDetails && expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'transitOrientedDevelopment' && (
            <div id="compact-development-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
              <h3 className="intervention-details-title">Compact Development</h3>
              <div className="intervention-details-content">
                <p style={{ marginBottom: 12 }}>
                  Compact Development is primarily a land use and housing-growth policy whose emissions effects occur through lower driving and denser urban form.
                </p>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>What this policy does</h4>
                <p>
                  Shifts housing growth into lower-VMT cities and compact locations. The model assumes cities with lower VMT per capita than the state average can absorb more growth. It estimates: added emissions from new homes; citywide density-related reductions; avoided out-of-boundary emissions from building in lower-VMT places; and net local and statewide impacts.
                </p>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>Key assumptions</h4>
                <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20 }}>
                  <li><strong>Compact-home emissions:</strong> The app uses a <em>location-specific</em> method: new units are assumed to generate driving (and emissions) in line with this location&apos;s VMT per capita relative to the state average (from the same LDV VMT and population data as the Transportation page).</li>
                  <li><strong>Density effect:</strong> Elasticity = -0.22; applied to full city LDV emissions (citywide, stock-based).</li>
                  <li><strong>1:1 displacement:</strong> Each new home built here replaces one that would have been built elsewhere in California (required for out-of-boundary savings).</li>
                  <li><strong>Accounting:</strong> {compactDevelopmentSeries?.mode === 'workbook_exact'
                    ? 'Workbook exact — all policy new units counted in avoided and added terms.'
                    : 'Incremental baseline — only units above BAU counted in avoided and added terms.'}
                  </li>
                </ul>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>Percent growth per year (same as main slider)</h4>
                <p style={{ marginBottom: 8 }}>
                  Same control as under VMT policies above. The <em>left</em> end of the range is BAU housing growth for this place (population-tracking), like the EV slider starts at current EV share. Higher growth adds incremental compact benefits; those are converted to VMT reduction % and included in the top-level <strong>VMT Reduction</strong> slider.
                </p>
                <div className="policy-main-control-section" style={{ marginTop: 8, marginBottom: 12 }}>
                  <div className="policy-main-label">
                    Percent growth per year ({compactDevelopmentSliderMinPercent.toFixed(1)}–{GROWTH_RATE_PERCENT_MAX}%)
                  </div>
                  <div className="policy-main-slider-container">
                    <input
                      type="range"
                      min={compactDevelopmentSliderMinPercent}
                      max={GROWTH_RATE_PERCENT_MAX}
                      step={0.1}
                      value={Math.min(
                        GROWTH_RATE_PERCENT_MAX,
                        Math.max(
                          compactDevelopmentSliderMinPercent,
                          subPolicyValues.transitOrientedDevelopment ?? compactDevelopmentSeries?.bauGrowthPercent ?? 0,
                        ),
                      )}
                      onChange={(e) => {
                        const raw = Number(e.target.value);
                        const v = Math.min(
                          GROWTH_RATE_PERCENT_MAX,
                          Math.max(compactDevelopmentSliderMinPercent, raw),
                        );
                        setSubPolicyValues((prev) => ({ ...prev, transitOrientedDevelopment: v }));
                      }}
                      className="policy-main-slider"
                    />
                    <div className="policy-main-value">
                      {Math.min(
                        GROWTH_RATE_PERCENT_MAX,
                        Math.max(
                          compactDevelopmentSliderMinPercent,
                          subPolicyValues.transitOrientedDevelopment ?? compactDevelopmentSeries?.bauGrowthPercent ?? 0,
                        ),
                      ).toFixed(1)}
                      %
                    </div>
                  </div>
                  <p style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    BAU floor for this location ≈ {compactDevelopmentSeries?.bauGrowthPercent != null ? `${compactDevelopmentSeries.bauGrowthPercent}%` : '…'} per year (housing/population trend). The separate “default growth” used only in the transport worksheet can add a VMT-based adjustment (see model notes).
                  </p>
                </div>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>Homes in or near downtown</h4>
                <p style={{ marginBottom: 8 }}>
                  Assume a share of homes are built in or near downtown (within half the distance to downtown than typical). These homes generate 29% fewer VMT than average; the slider modifies the forecast accordingly.
                </p>
                <div className="policy-main-control-section" style={{ marginTop: 8, marginBottom: 12 }}>
                  <div className="policy-main-label">Homes in or near downtown (%)</div>
                  <div className="policy-main-slider-container">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={subPolicyValues.downtownHomesPercent ?? 70}
                      onChange={(e) => setSubPolicyValues((prev) => ({ ...prev, downtownHomesPercent: Number(e.target.value) }))}
                      className="policy-main-slider"
                    />
                    <div className="policy-main-value">
                      {subPolicyValues.downtownHomesPercent ?? 70}%
                    </div>
                  </div>
                  <p style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Default 70%. Effective VMT reduction from this option = 29% × this share (e.g. 70% → ~20% reduction).
                  </p>
                </div>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>Interpretation</h4>
                <p>
                  Local emissions may rise (more homes, more travel). Statewide emissions can still fall because households in compact, lower-VMT cities drive less than average Californians. Charts report both annual and cumulative net local and net statewide impacts from the same computed series.
                </p>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>Limitations</h4>
                <p>
                  Policy in practice: update zoning and encourage compact, mixed-use housing near downtown and transit. This typically increases community emissions in absolute terms while reducing statewide VMT and GHG (Scope 4). Long term, density can lower community-wide emissions via supportive infrastructure and alternative travel. Results depend on the 1:1 displacement and citywide density assumptions.
                </p>

                {/* Recommendation: VMT below state average AND density > 3000 */}
                {compactDevelopmentSeries && (
                  <div
                    className={`policy-compact-recommendation ${compactDevelopmentSeries.recommendedForLocation ? 'policy-compact-recommendation--ok' : 'policy-compact-recommendation--warn'}`}
                    style={{
                      marginTop: 16,
                      padding: '12px 14px',
                      borderRadius: 8,
                    }}
                  >
                    {compactDevelopmentSeries.recommendedForLocation ? (
                      <>
                        <strong>Recommended for this location.</strong>{' '}
                        {compactDevelopmentSeries.densityDataMissing ? (
                          <>VMT per capita {compactDevelopmentSeries.vmtPercentBelowState > 0 ? `${compactDevelopmentSeries.vmtPercentBelowState}% below state average. ` : ''}Density data not available for this geography; policy is generally best in denser areas.</>
                        ) : (
                          <>VMT per capita {compactDevelopmentSeries.vmtPercentBelowState > 0 ? `${compactDevelopmentSeries.vmtPercentBelowState}% below state average` : 'below state average'} and density ≥ {COMPACT_DEVELOPMENT_MIN_DENSITY.toLocaleString()} per sq mi.
                            {compactDevelopmentSeries.populationDensityPerSqMi != null && (
                              <> ({compactDevelopmentSeries.populationDensityPerSqMi.toLocaleString(undefined, { maximumFractionDigits: 0 })} per sq mi.)</>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <strong>Not recommended.</strong> VMT per capita {compactDevelopmentSeries.vmtPercentAboveState > 0 ? `${compactDevelopmentSeries.vmtPercentAboveState}% above state average` : 'at or above state average'}, or density is &lt;{COMPACT_DEVELOPMENT_MIN_DENSITY.toLocaleString()} per sq mi (or unknown). Best applied in lower-VMT, denser areas.
                      </>
                    )}
                  </div>
                )}

                {results?.baseline?.lightDutyGHG != null && (
                  <p style={{ marginTop: 16, fontSize: '0.9rem' }}>
                    <strong>Impact on forecast.</strong> The headline GHG forecast uses <em>net statewide savings</em> from this model when data are available (includes out-of-boundary savings). Incremental compact benefits are also reflected in the top <strong>VMT Reduction</strong> % (≈{compactVmtReductionPercent.toFixed(1)}% from this policy). Compact LDV reduction in the policy year ≈ {Math.round((results.contributionsByPolicy?.vmtReductionByComponent?.transitOrientedDevelopment ?? 0)).toLocaleString()} MT CO₂e. Charts below use the same series.
                  </p>
                )}

                <details style={{ marginTop: 12, fontSize: '0.85rem' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>How calculated</summary>
                  <ul style={{ marginTop: 6, paddingLeft: 20 }}>
                    <li>Growth rate = (BAU growth % + % below state average) / 100, or slider override. Savings = difference between BAU and with policy.</li>
                    <li>New units (policy) = growth rate × housing stock; additional = policy − BAU.</li>
                    <li>Compact-home emissions = location VMT/cap ÷ state VMT/cap × state GHG per home (when city &lt; state).</li>
                    <li>Out-of-boundary savings = additional units × (state GHG per home − compact per home).</li>
                    <li>Density savings = − city LDV × growth rate × 0.22. Added local = additional units × compact per home.</li>
                    <li>Net local = density savings − added local; net statewide = out-of-boundary + density savings − added local. Cumulatives = sum of annual values.</li>
                  </ul>
                </details>

                {compactDevelopmentSeries && compactDevelopmentSeries.rows.length > 0 && (() => {
                  const series = compactDevelopmentSeries;
                  const rows = series.rows;
                  const last = rows[rows.length - 1];
                  const lastYear = last.year;
                  const numYears = rows.length;
                  const avgNewUnitsBau = numYears > 0 ? rows.reduce((s, r) => s + r.newUnitsBau, 0) / numYears : 0;
                  const avgHousingStock = numYears > 0 ? rows.reduce((s, r) => s + r.housingUnitsBau, 0) / numYears : 0;
                  const hasCityData = avgHousingStock >= 1 || last.cityLdvGhg > 0;
                  const pctStock = avgHousingStock > 0 ? (avgNewUnitsBau / avgHousingStock) * 100 : 0;
                  const avgNewUnitsPolicy = numYears > 0 ? rows.reduce((s, r) => s + r.newUnitsPolicy, 0) / numYears : 0;
                  const projected20YrLocal = numYears > 0 ? (last.netLocalCumulative / numYears) * 20 : 0;
                  const projected20YrState = numYears > 0 ? (last.netStatewideCumulative / numYears) * 20 : 0;
                  const vmtPctBelowState = Math.round(last.vmtBelowState * 100);
                  const currentBuildPhrase = Math.round(avgNewUnitsBau) >= 10 ? Math.round(avgNewUnitsBau).toLocaleString() : avgNewUnitsBau.toFixed(1);
                  const compactTargetPhrase = Math.round(avgNewUnitsPolicy) >= 10 ? Math.round(avgNewUnitsPolicy).toLocaleString() : avgNewUnitsPolicy.toFixed(1);
                  const localTons = Math.abs(Math.round(projected20YrLocal));
                  const stateTons = Math.abs(Math.round(projected20YrState));
                  // netLocal = local added − density avoided; > 0 means local emissions increase
                  const localIncrease = projected20YrLocal > 0;
                  // netStatewide = statewide avoided + local added − density avoided; < 0 means statewide reduction
                  const stateReduction = projected20YrState < 0;
                  const driveLessPhrase = vmtPctBelowState > 0
                    ? `since ${location} residents drive ${vmtPctBelowState}% less than average Californians`
                    : `; ${location} residents drive at or above the state average`;
                  const localSentence = localIncrease
                    ? `While this will lead to a net increase in local GHG emissions of ${localTons.toLocaleString()} tons of CO₂e over the next 20 years, `
                    : `This leads to a net decrease in local GHG emissions of ${localTons.toLocaleString()} tons of CO₂e over the next 20 years. `;
                  const stateSentence = stateReduction
                    ? `statewide emissions will be reduced by ${stateTons.toLocaleString()} tons ${driveLessPhrase}.`
                    : `statewide emissions would increase by ${stateTons.toLocaleString()} tons ${driveLessPhrase}.`;
                  if (!hasCityData) {
                    return (
                      <div style={{ marginTop: 16 }}>
                        <p style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>
                          <strong>Summary for {location}:</strong> City-level housing or emissions data is not available for this location in the compact development model, so the summary cannot be computed. The chart below may show zeros. If you expect data for this city, it may be stored under a different geography type or geofips; try another location or ensure data has been loaded for this city.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div style={{ marginTop: 16 }}>
                      <p style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>
                        <strong>Summary for {location}:</strong> {location} currently builds about {currentBuildPhrase} new homes per year, or {pctStock.toFixed(2)}% of the current housing stock. Meeting compact development goals would require {compactTargetPhrase} homes per year over the next 20 years. {localSentence}{stateSentence}
                      </p>
                      <p style={{ marginTop: 8, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        These 20-year totals match the <strong>Cumulative local, statewide and net</strong> chart below (values at the end of the period). Annual charts can show negative (red) years early on; the cumulative chart shows the net effect over time.
                      </p>
                      <p style={{ marginTop: 4, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        Modeled period: through {lastYear}. Years after the last data year use projected BAU growth and last-known city/state emissions and VMT.
                      </p>
                    </div>
                  );
                })()}

                <CompactDevelopmentCharts
                  location={location}
                  series={compactDevelopmentSeries}
                  loading={compactDevelopmentLoading}
                />
              </div>
            </div>
          )}

          {canExpandDetails && expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'micromobility' && (
            <div id="micromobility-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
              <h3 className="intervention-details-title">Expand Micromobility</h3>
              <div className="intervention-details-content">
                  {/* Top full-width control mirroring the menu slider */}
                  <div className="policy-main-control-section" style={{ marginBottom: 16 }}>
                    <div className="policy-main-label">Expand micromobility by</div>
                    <div className="policy-main-slider-container">
                      <input
                        type="range"
                        min={0}
                        max={200}
                        step={5}
                        value={subPolicyValues.micromobility}
                        onChange={(e) => setSubPolicyValues(prev => ({ ...prev, micromobility: Number(e.target.value) }))}
                        className="policy-main-slider"
                      />
                      <div className="policy-main-value">{subPolicyValues.micromobility}%</div>
                    </div>
                  </div>
                  <p>
                    This measure estimates reduced GHG emissions from trips shifted to shared e-bikes and e-scooters and
                    subtracts added electricity emissions. Default assumptions reflect findings that roughly one-third of
                    micromobility trips displace motor vehicle trips. Users can scale program intensity with the slider in the menu.
                  </p>

                  {micromobilityStats && (
                    <div className="policy-results-grid">
                      <div className="result-item">
                        <span className="result-label">Population:</span>
                        <span className="result-value">{micromobilityStats.population.toLocaleString()}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Program Scale:</span>
                        <span className="result-value">{micromobilityStats.scalePercent}%</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Trips per Resident:</span>
                        <span className="result-value">{micromobilityStats.tripsPerResident}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Total Program Trips:</span>
                        <span className="result-value">{micromobilityStats.totalProgramTrips.toLocaleString()}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Annual Bike Miles:</span>
                        <span className="result-value">{micromobilityStats.bikeMiles.toLocaleString()}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Annual Scooter Miles:</span>
                        <span className="result-value">{micromobilityStats.scooterMiles.toLocaleString()}</span>
                      </div>
                      <div className="result-item highlight">
                        <span className="result-label">VMT Reduced:</span>
                        <span className="result-value">{micromobilityStats.displacedCarMiles.toLocaleString()} miles</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">EV Bike+Scooter Electricity:</span>
                        <span className="result-value">{micromobilityStats.electricityKwh.toLocaleString()} kWh</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Tons Avoided from Cars:</span>
                        <span className="result-value">{micromobilityStats.avoidedCarTons.toLocaleString()}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Tons Added from Electricity:</span>
                        <span className="result-value">{micromobilityStats.electricityTons.toLocaleString()}</span>
                      </div>
                      <div className="result-item highlight">
                        <span className="result-label">Net GHG Reduction:</span>
                        <span className="result-value">{micromobilityStats.netTons.toLocaleString()} MT CO₂e</span>
                      </div>
                    </div>
                  )}

                  {!micromobilityStats && (
                    <p style={{ opacity: 0.7 }}>Loading micromobility statistics…</p>
                  )}
                </div>
              </div>
            )}

          {canExpandDetails && expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'activeTransportation' && (
            <div id="active-transportation-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
              <h3 className="intervention-details-title">Active transportation infrastructure (Under construction)</h3>
              <div className="intervention-details-content">
                <p
                  style={{
                    margin: '0 0 1rem',
                    padding: '0.75rem 1rem',
                    borderRadius: 8,
                    background: 'var(--section-bg, #f8fafc)',
                    border: '1px solid var(--border-color, #e2e8f0)',
                    color: 'var(--text-secondary, #64748b)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                  }}
                >
                  Detailed maps and documentation for this policy are under construction. You can still adjust{' '}
                  <strong>Expand active transportation infrastructure</strong> in the policy menu above; those values
                  continue to feed the GHG forecast.
                </p>
              </div>
            </div>
          )}

          {canExpandDetails && expandedIntervention === 'vehicleElectrification' && (
            <div id="vehicle-electrification-details" className="policy-intervention-details-section">
              <h3 className="intervention-details-title">Vehicle Electrification Policy</h3>
              <div className="intervention-details-content">
                <VehicleElectrificationPolicy
                  emfacVersion={emfacVersion}
                  currentEVPercent={results?.baseline.currentEVPercent || 0}
                  currentEVPercent2023={results?.baseline.currentEVPercent2023}
                  beyondBauPercent={interventionAreas.vehicleElectrification}
                  targetEVPercent={
                    beyondBauEvSliderToAbsolute(
                      interventionAreas.vehicleElectrification,
                      results?.baseline.currentEVPercent || 0
                    )
                  }
                  location={location}
                  results={results}
                  solarCapacityMultiplier={scenario.solarCapacityMultiplier}
                />
              </div>
            </div>
          )}

          {canExpandDetails && expandedIntervention === 'organicsDiversion' && (
            <div id="organics-diversion-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
              <h3 className="intervention-details-title">Organics diversion (Under construction)</h3>
              <div className="intervention-details-content">
                <p
                  style={{
                    margin: 0,
                    padding: '0.75rem 1rem',
                    borderRadius: 8,
                    background: 'var(--section-bg, #f8fafc)',
                    border: '1px solid var(--border-color, #e2e8f0)',
                    color: 'var(--text-secondary, #64748b)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                  }}
                >
                  Charts and assumptions for organics diversion are under construction. You can still set{' '}
                  <strong>Organics Diversion</strong> (additional diversion beyond BAU) in the policy menu; that target
                  continues to drive waste-sector modeling in the forecast.
                </p>
              </div>
            </div>
          )}

          {canExpandDetails && expandedIntervention === 'buildingsElectrification' && (
            <div id="building-electrification-details" className="policy-intervention-details-section">
              <h3 className="intervention-details-title">Building Electrification Policy</h3>
              <div className="intervention-details-content">
                <BuildingElectrificationPolicy
                  additionalElectrificationPercent={interventionAreas.buildingsElectrification ?? scenario.heatPumpAdoptionPercent ?? 0}
                  results={results}
                  bauTherms2045={results?.baseline.bauTherms2045}
                  bauKwh2045={results?.baseline.bauKwh2045}
                  rngShare2045={results?.baseline.rngShare2045}
                  bauElectrifiedShare2045={results?.baseline.bauElectrifiedShare2045}
                  bauElectrifiedShareResidential2045={results?.baseline.bauElectrifiedShareResidential2045}
                  bauElectrifiedShareCommercial2045={results?.baseline.bauElectrifiedShareCommercial2045}
                  location={location}
                  buildingsBauScenario={buildingsBauScenario}
                  buildingsRngPercent={buildingsRngPercent}
                  onBuildingsRngChange={setBuildingsRngPercent}
                  buildingsCop={buildingsCop}
                  onBuildingsCopChange={setBuildingsCop}
                  buildingEmissionsTimeSeries={buildingEmissionsTimeSeries}
                  cleanElectricityYear={(scenario as { gridCleanElectricityYear?: number }).gridCleanElectricityYear ?? 2045}
                  billImpactDiagnostics={decisionSummary?.buildingElectrificationBillImpact}
                  priceDiagnostics={decisionSummary?.energyPriceDiagnostics}
                  breakEvenElectricityPricePerKwh={decisionSummary?.breakEvenElectricityPrice}
                  buildingEnergyComparisonSeries={buildingEnergyComparisonSeries}
                  heatPumpAdoptionPercent2045={
                    interventionAreas.buildingsElectrification ?? scenario.heatPumpAdoptionPercent ?? 0
                  }
                  buildingEndUseAssumptions={scenario.buildingEndUseAssumptions ?? undefined}
                  onBuildingEndUseAssumptionsChange={(next) => {
                    setScenario((prev) => ({ ...prev, buildingEndUseAssumptions: next }));
                  }}
                />
              </div>
            </div>
          )}

          {canExpandDetails && expandedIntervention === 'energyEfficiency' && (
            <div id="energy-efficiency-details" className="policy-intervention-details-section">
              <h3 className="intervention-details-title">Energy Efficiency</h3>
              <div className="intervention-details-content">
                <p style={{ marginBottom: 12 }}>
                  Local energy efficiency reduces building demand (therms and kWh) before additional electrification is applied.
                  The stock model splits <strong>existing</strong> buildings (current intensities, optional BAU efficiency ramp to 2045) from <strong>new</strong> growth (all-electric new construction; new residential standards apply only to new homes).
                  Existing residential and commercial retrofits ramp linearly from 2023 to the percent you set for 2045.
                </p>
                <p style={{ marginBottom: 12, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  Order: BAU demand → efficiency layer → additional building electrification beyond BAU → RNG and grid carbon intensity.
                </p>
                <p style={{ marginBottom: 12, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Higher efficiency reduces total energy demand, including fossil energy available for electrification. As a result, very high efficiency can reduce the apparent impact of electrification policies.
                </p>
                <p style={{ marginBottom: 16, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  <strong>Other / unmodeled efficiency</strong> is the residual to reconcile the top-level efficiency slider with the named levers when the panel is collapsed.
                </p>

                <EnergyEfficiencyOverTimeChart data={efficiencyChartData} />
                <EfficiencyByPolicyChart contributions={efficiencyContributions2045} yearLabel="2045" />

                {location && /Berkeley/i.test(location) && results?.baseline && (
                  <details style={{ marginTop: 16, padding: 12, background: 'var(--section-bg-alt)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Berkeley validation</summary>
                    <div style={{ marginTop: 8, fontSize: '0.8125rem' }}>
                      <p style={{ marginBottom: 6, fontWeight: 600 }}>Existing vs new share (residential)</p>
                      <p style={{ marginBottom: 4 }}>2030: existing {efficiencySegmentShares ? (efficiencySegmentShares.resExisting2030 * 100).toFixed(1) : '—'}%, new {efficiencySegmentShares ? ((1 - efficiencySegmentShares.resExisting2030) * 100).toFixed(1) : '—'}%</p>
                      <p style={{ marginBottom: 12 }}>2045: existing {efficiencySegmentShares ? (efficiencySegmentShares.resExisting2045 * 100).toFixed(1) : '—'}%, new {efficiencySegmentShares ? ((1 - efficiencySegmentShares.resExisting2045) * 100).toFixed(1) : '—'}%</p>
                      <p style={{ marginBottom: 6, fontWeight: 600 }}>Existing vs new share (commercial)</p>
                      <p style={{ marginBottom: 4 }}>2030: existing {efficiencySegmentShares ? (efficiencySegmentShares.comExisting2030 * 100).toFixed(1) : '—'}%, new {efficiencySegmentShares ? ((1 - efficiencySegmentShares.comExisting2030) * 100).toFixed(1) : '—'}%</p>
                      <p style={{ marginBottom: 12 }}>2045: existing {efficiencySegmentShares ? (efficiencySegmentShares.comExisting2045 * 100).toFixed(1) : '—'}%, new {efficiencySegmentShares ? ((1 - efficiencySegmentShares.comExisting2045) * 100).toFixed(1) : '—'}%</p>
                      <p style={{ marginBottom: 6, fontWeight: 600 }}>BAU vs efficiency-adjusted 2045</p>
                      <p style={{ marginBottom: 4 }}>BAU therms: {(results.baseline.bauTherms2045 ?? results.baseline.naturalGasTherms ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                      <p style={{ marginBottom: 4 }}>BAU kWh: {(results.baseline.bauKwh2045 ?? results.baseline.electricityKwh ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                      {efficiencyChartData && efficiencyChartData.years.indexOf(2045) >= 0 && (
                        <>
                          <p style={{ marginBottom: 4 }}>Efficiency-adjusted therms: {(efficiencyChartData.effTherms[efficiencyChartData.years.indexOf(2045)] ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                          <p style={{ marginBottom: 12 }}>Efficiency-adjusted kWh: {(efficiencyChartData.effKwh[efficiencyChartData.years.indexOf(2045)] ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                        </>
                      )}
                      <p style={{ marginBottom: 4, fontWeight: 600 }}>Actual total efficiency % from component sliders</p>
                      <p>{(scenario as any).energyEfficiencyTargetPercent ?? (scenario as any).energyEfficiencyPercent ?? 0}%</p>
                    </div>
                  </details>
                )}

                <details
                  className="policy-energy-efficiency-assumptions-details"
                  style={{
                    marginTop: 24,
                    padding: 14,
                    background: 'var(--section-bg-alt)',
                    borderRadius: 8,
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <summary
                    style={{
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: '1rem',
                      listStyle: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span className="policy-ee-assumptions-chevron" aria-hidden="true">
                      ▶
                    </span>
                    Assumptions
                  </summary>
                  <div style={{ marginTop: 16, paddingTop: 4, borderTop: '1px solid var(--border-color)' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '0.9375rem', fontWeight: 600 }}>BAU efficiency (existing buildings)</h4>
                    <p style={{ margin: '0 0 12px', fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      Background efficiency improvement on the <strong>existing 2023 stock</strong> through 2045 (linear), before your
                      policy retrofit sliders. Defaults by Buildings BAU scenario: Conservative{' '}
                      {bauExistingStockEfficiencyPercentBy2045('conservative')}%, Current policy{' '}
                      {bauExistingStockEfficiencyPercentBy2045('current_policy')}%, Accelerated{' '}
                      {bauExistingStockEfficiencyPercentBy2045('accelerated')}%.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: 12 }}>
                      <input
                        id="ee-bau-existing-eff-toggle"
                        type="checkbox"
                        checked={scenario.applyBauExistingStockEfficiency !== false}
                        onChange={(e) =>
                          setScenario((prev) => ({
                            ...prev,
                            applyBauExistingStockEfficiency: e.target.checked,
                          }))
                        }
                      />
                      <label htmlFor="ee-bau-existing-eff-toggle" style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                        Apply BAU efficiency improvement to existing buildings by 2045
                      </label>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8125rem', maxWidth: 200 }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Cumulative efficiency by 2045 (%)</span>
                        <input
                          type="number"
                          min={0}
                          max={40}
                          step={1}
                          disabled={scenario.applyBauExistingStockEfficiency === false}
                          value={
                            scenario.bauExistingStockEfficiencyPercent2045 != null &&
                            Number.isFinite(scenario.bauExistingStockEfficiencyPercent2045)
                              ? Math.round(scenario.bauExistingStockEfficiencyPercent2045)
                              : bauExistingStockEfficiencyPercentBy2045(buildingsBauScenario)
                          }
                          onChange={(e) => {
                            const v = Math.max(0, Math.min(40, Number(e.target.value) || 0));
                            setScenario((prev) => ({ ...prev, bauExistingStockEfficiencyPercent2045: v }));
                          }}
                          style={{
                            width: 88,
                            padding: '6px 8px',
                            border: '1px solid var(--border-color)',
                            borderRadius: 6,
                            background: 'var(--chart-container-bg)',
                            opacity: scenario.applyBauExistingStockEfficiency === false ? 0.5 : 1,
                          }}
                        />
                      </label>
                    </div>
                    <p style={{ margin: '0 0 20px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Requires stock model data (housing + establishments).
                    </p>

                    <h4 style={{ margin: '0 0 10px', fontSize: '0.9375rem', fontWeight: 600 }}>Retrofit program costs</h4>
                    <p style={{ margin: '0 0 12px', fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      Gross program costs; net retrofit CAPEX in the cost model subtracts <strong>Weatherization rebate</strong> and{' '}
                      <strong>Commercial retrofit rebate</strong> from the Cost Assumptions section below.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8125rem' }}>
                        <span>Residential retrofit ($ / participating home)</span>
                        <input
                          type="number"
                          min={0}
                          step={500}
                          value={policyCostAssumptions.weatherizationCostPerParticipatingHome}
                          onChange={(e) =>
                            setPolicyCostAssumptions((a) => ({
                              ...a,
                              weatherizationCostPerParticipatingHome: Number(e.target.value) || 0,
                            }))
                          }
                          style={{
                            maxWidth: 120,
                            padding: '6px 8px',
                            border: '1px solid var(--border-color)',
                            borderRadius: 6,
                            background: 'var(--chart-container-bg)',
                          }}
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8125rem' }}>
                        <span>Commercial retrofit ($ / participating establishment)</span>
                        <input
                          type="number"
                          min={0}
                          step={1000}
                          value={policyCostAssumptions.commercialRetrofitCostPerParticipatingEstablishment}
                          onChange={(e) =>
                            setPolicyCostAssumptions((a) => ({
                              ...a,
                              commercialRetrofitCostPerParticipatingEstablishment: Number(e.target.value) || 0,
                            }))
                          }
                          style={{
                            maxWidth: 120,
                            padding: '6px 8px',
                            border: '1px solid var(--border-color)',
                            borderRadius: 6,
                            background: 'var(--chart-container-bg)',
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          )}

          {canExpandDetails && expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'transitImprovements' && (
            <div id="transit-service-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
              <h3 className="intervention-details-title">Extend Transit Network Coverage of Miles</h3>
              <div className="intervention-details-content">
                {/* Top full-width control mirroring the menu slider */}
                <div className="policy-main-control-section" style={{ marginBottom: 16 }}>
                  <div className="policy-main-label">Expand transit service by</div>
                  <div className="policy-main-slider-container">
                    <input
                      type="range"
                      min={0}
                      max={50}
                      step={1}
                      value={subPolicyValues.transitImprovements}
                      onChange={(e) => setSubPolicyValues(prev => ({ ...prev, transitImprovements: Number(e.target.value) }))}
                      className="policy-main-slider"
                    />
                    <div className="policy-main-value">{subPolicyValues.transitImprovements}%</div>
                  </div>
                </div>

                <p>
                  This measure will expand the local transit network by either adding or modifying existing service or
                  extending operating hours near the project area. Earlier starts and later end times can accommodate
                  alternative-shift workers, encouraging transit use and reducing VMT and associated GHG emissions.
                </p>

                {(() => {
                  const percentIncrease = subPolicyValues.transitImprovements || 0; // %
                  const vmtReductionPctRounded = calculateTransitVmtReductionPercent(percentIncrease);
                  const yearsTo2045 = TRANSIT_PROJECTION_END_YEAR - TRANSIT_IMPLEMENTATION_YEAR + 1;
                  const ldvBaseline = results?.baseline.lightDutyGHG || 0;
                  const estGHGReduction2045 = Math.round(ldvBaseline * yearsTo2045 * (vmtReductionPctRounded / 100));
                  const baselineServiceMiles = transitRevenueMiles?.miles;
                  const baselineYear = transitRevenueMiles?.year;
                  const totalVmt = results?.baseline.vmt || 0;
                  const vmtReducedMiles = Math.round(totalVmt * (vmtReductionPctRounded / 100));
                  return (
                    <div>
                      <p style={{ marginTop: 8 }}>
                        <strong>Smart narrative:</strong> {location}
                        {baselineServiceMiles ? ` has ${baselineServiceMiles.toLocaleString()} transit service miles in ${baselineYear}.` : ''}
                        {` If ${location} increases transit service miles by ${percentIncrease}%, total GHG emissions from light-duty vehicles by 2045 are estimated to be reduced by ${estGHGReduction2045.toLocaleString()} tCO₂e.`}
                      </p>
                      <div className="policy-results-grid" style={{ marginTop: 12 }}>
                        <div className="result-item"><span className="result-label">Elasticity:</span><span className="result-value">{TRANSIT_ELASTICITY}</span></div>
                        <div className="result-item"><span className="result-label">Transit mode share:</span><span className="result-value">{TRANSIT_MODE_SHARE_PERCENT}%</span></div>
                        <div className="result-item"><span className="result-label">Statewide mode shift factor:</span><span className="result-value">{TRANSIT_STATEWIDE_MODE_SHIFT_PERCENT}%</span></div>
                        <div className="result-item highlight"><span className="result-label">VMT reduction in year of implementation:</span><span className="result-value">{vmtReductionPctRounded.toFixed(3)}%</span></div>
                        <div className="result-item highlight"><span className="result-label">Total VMT reduced (year of implementation):</span><span className="result-value">{vmtReducedMiles.toLocaleString()} miles</span></div>
                      </div>
                      <p style={{ marginTop: 8, opacity: 0.75 }}>
                        Sources: FHWA 2017; Handy et al. 2013.
                      </p>
                      <TransitServiceExpansionCharts
                        location={location}
                        historicalServiceMiles={transitServiceHistory}
                        percentIncrease={percentIncrease}
                        baselineServiceMiles={baselineServiceMiles}
                        baselineYear={baselineYear}
                        implementationYear={TRANSIT_IMPLEMENTATION_YEAR}
                        projectionEndYear={TRANSIT_PROJECTION_END_YEAR}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {decisionSummary && (
            <details
              style={{
                marginTop: 24,
                marginBottom: 16,
                padding: 16,
                border: '1px solid var(--border-color)',
                borderRadius: 10,
                background: 'var(--section-bg-alt)',
              }}
            >
              <summary style={{ cursor: 'pointer', fontSize: '1.05rem', fontWeight: 700 }}>Decision Summary</summary>
              <div style={{ marginTop: 10 }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                  All values represent emissions reductions relative to baseline.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{decisionSummary.headline}</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700 }}>{decisionSummary.headlineValue}</div>
                  </div>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Cost per ton</div>
                    <div style={{ fontSize: '0.98rem', fontWeight: 600 }}>
                      {decisionSummary.costPerTon != null ? `${currencyShort(decisionSummary.costPerTon)} / tCO2e` : '—'}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{decisionSummary.costPerTonLabel}</div>
                  </div>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Payback</div>
                    <div style={{ fontSize: '0.98rem', fontWeight: 600 }}>
                      {decisionSummary.paybackYear != null ? decisionSummary.paybackYear : 'No payback by 2045'}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{decisionSummary.paybackInterpretation}</div>
                  </div>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Total capital investment</div>
                    <div style={{ fontSize: '0.98rem', fontWeight: 600 }}>{currencyShort(decisionSummary.totalCapitalInvestment)}</div>
                  </div>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Dominant cost driver</div>
                    <div style={{ fontSize: '0.98rem', fontWeight: 600, textTransform: 'capitalize' }}>
                      {decisionSummary.dominantCostDriver}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 10, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  <p style={{ margin: '0 0 6px 0' }}>{decisionSummary.driverExplanation}</p>
                  <p style={{ margin: 0 }}>{decisionSummary.capexVsSavingsExplanation}</p>
                </div>
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>State vs Local Emissions Reduction (tCO2e, % of total)</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      <div>State/background reductions: {Math.round(decisionSummary.stateLocal.stateReductionTons).toLocaleString()} tCO2e ({decisionSummary.stateLocal.stateReductionPercent.toFixed(1)}%)</div>
                      <div>Local policy reductions: {Math.round(decisionSummary.stateLocal.localReductionTons).toLocaleString()} tCO2e ({decisionSummary.stateLocal.localReductionPercent.toFixed(1)}%)</div>
                      <div>State/background NPV: {currencyShort(decisionSummary.stateLocal.stateNpv)}</div>
                      <div>Local incremental NPV: {currencyShort(decisionSummary.stateLocal.localNpv)}</div>
                    </div>
                  </div>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Sector Emissions Reduction (tCO2e, % of total)</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {decisionSummary.sectors.map((s) => (
                        <div key={s.key}>
                          {s.label}: {Math.round(s.tons).toLocaleString()} tCO2e ({s.percent.toFixed(1)}%)
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Policy Lever Emissions Reduction (tCO2e, % of local)</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {decisionSummary.localPolicyRows.map((p) => (
                        <div key={p.key}>
                          {p.label}: {Math.round(p.tons).toLocaleString()} tCO2e ({p.percent.toFixed(1)}%)
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Top Individual Policy Emissions Reduction (tCO2e)</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {decisionSummary.topPolicyImpacts.slice(0, 6).map((p, idx) => (
                        <div key={p.key}>
                          {idx + 1}. {p.label}: {Math.round(p.tons).toLocaleString()} tCO2e
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Building Electrification Bill Impact</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      <div>
                        Citywide total annual bill impact (2045):{' '}
                        {currencyShort(decisionSummary.buildingElectrificationBillImpact.citywideTotalAnnualBillImpact)} / year
                      </div>
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                        Scope: all modeled building gas + electricity (residential + commercial).
                      </div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                        Note: <strong>Annual Cash Flow</strong> in the cost section combines <strong>buildings utility</strong> with{' '}
                        <strong>transport fuel</strong> savings/costs—so its green “operating” bars can be positive overall even when
                        building bills rise.
                      </div>
                      <div>Monthly (citywide total): {currencyShort(decisionSummary.buildingElectrificationBillImpact.monthlyCitywideTotalBillImpact)} / month</div>
                      <div>
                        Cumulative bill change (to 2045): {currencyShort(decisionSummary.buildingElectrificationBillImpact.cumulativeBillImpact)}
                      </div>
                      {decisionSummary.buildingElectrificationBillImpact.billImpactDetail && (
                        <>
                          <div style={{ marginTop: 8, fontWeight: 600 }}>Per housing unit (2045)</div>
                          {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.citywideAverageBillImpactPerHousingUnitAnnual != null && (
                            <div>
                              Spread of citywide total across all homes:{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail
                                  .citywideAverageBillImpactPerHousingUnitAnnual,
                              )}
                              / home-year (num{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail.citywidePerHousingUnit
                                  .numeratorAnnual,
                              )}{' '}
                              ÷ {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.citywidePerHousingUnit.denominatorHousingUnits?.toLocaleString() ?? '—'}{' '}
                              homes)
                            </div>
                          )}
                          {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.averageAcrossAllHomesAnnualBillImpact != null && (
                            <div>
                              Average across all homes (residential-attributed):{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail
                                  .averageAcrossAllHomesAnnualBillImpact,
                              )}
                              / home-year (num{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail.allHomesResidentialAttributed
                                  .numeratorAnnual,
                              )}{' '}
                              ÷ {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.allHomesResidentialAttributed.denominatorHousingUnits?.toLocaleString() ?? '—'}{' '}
                              homes)
                            </div>
                          )}
                          {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.averageForAffectedHomesAnnualBillImpact != null && (
                            <div>
                              Average for affected homes:{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail
                                  .averageForAffectedHomesAnnualBillImpact,
                              )}
                              / home-year (num{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail.affectedHomesResidentialAttributed
                                  .numeratorAnnual,
                              )}{' '}
                              ÷ {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.affectedHomesResidentialAttributed.denominatorElectrifiedHomes.toLocaleString()}{' '}
                              electrified homes)
                            </div>
                          )}
                          <div style={{ marginTop: 6, fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
                            Therms reduced (2045):{' '}
                            {Math.round(
                              decisionSummary.buildingElectrificationBillImpact.billImpactDetail.annualThermsReduced2045,
                            ).toLocaleString()}
                            {' · '}
                            kWh added (2045):{' '}
                            {Math.round(
                              decisionSummary.buildingElectrificationBillImpact.billImpactDetail.annualKwhAdded2045,
                            ).toLocaleString()}
                          </div>
                        </>
                      )}
                      <div style={{ marginTop: 4, fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
                        Positive values indicate bill increases; negative values indicate bill decreases.
                      </div>
                    </div>
                  </div>
                  <div style={{ background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Comparable Energy Price Diagnostics</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      <div>Electricity price: ${decisionSummary.energyPriceDiagnostics.electricityPricePerKwh.toFixed(3)} / kWh</div>
                      <div>Natural gas price: ${decisionSummary.energyPriceDiagnostics.gasPricePerTherm.toFixed(3)} / therm</div>
                      <div>Natural gas electric-equivalent price: ${decisionSummary.energyPriceDiagnostics.gasEquivalentPricePerKwh.toFixed(3)} / kWh-e</div>
                      <div>Break-even electricity price: ${decisionSummary.breakEvenElectricityPrice.toFixed(3)} / kWh</div>
                      <div style={{ marginTop: 4, fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
                        Source: {decisionSummary.energyPriceDiagnostics.priceSource}. Formula: gas_price / (29.3 / COP), COP {buildingsCop.toFixed(1)}.
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 12, background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Policy Intelligence Narrative</div>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: 'var(--text-primary)' }}>{decisionSummary.narratives.transportDominanceExplanation}</p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Local policy sector ranking: {decisionSummary.localSectors
                      .slice()
                      .sort((a, b) => b.tons - a.tons)
                      .map((s) => `${s.label} ${s.percent.toFixed(1)}%`)
                      .join(' · ')}
                  </p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: 'var(--text-primary)' }}>{decisionSummary.narratives.buildingsCostDynamicsExplanation}</p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: 'var(--text-primary)' }}>{decisionSummary.narratives.electrificationOperatingCostNarrative}</p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: 'var(--text-primary)' }}>{decisionSummary.narratives.capexVsOpexNarrative}</p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: 'var(--text-primary)' }}>{decisionSummary.narratives.rateStructureNarrative}</p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: 'var(--text-primary)' }}>{decisionSummary.narratives.recommendationNarrative[0]}</p>
                  <p style={{ margin: 0, fontSize: '0.84rem', color: 'var(--text-primary)' }}>{decisionSummary.narratives.recommendationNarrative[1]}</p>
                </div>
                <div style={{ marginTop: 12, background: 'var(--chart-container-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>BAU Assumptions Summary</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    <div>Transport: {decisionSummary.bauAssumptions.transport}</div>
                    <div>Buildings: {decisionSummary.bauAssumptions.buildings}</div>
                    <div>Electricity decarbonization: {decisionSummary.bauAssumptions.electricity}</div>
                    <div>Waste: {decisionSummary.bauAssumptions.waste}</div>
                  </div>
                </div>
              </div>
            </details>
          )}

          {/* Portfolio Cost & Savings — collapsed by default */}
          {results && (
            <PortfolioCostSection
              buildingsCostResults={buildingsCostResults}
              transportCostResults={transportCostResults}
              population={getCachedBaseline(`baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`)?.population ?? null}
              location={location}
              scenario={scenario}
              costView={portfolioCostView}
              onCostViewChange={setPortfolioCostView}
              emfacVersion={emfacVersion}
              buildingsBauScenario={buildingsBauScenario}
              defaultCollapsed
            />
          )}

          {/* Scenario Cost Comparison — same scenario across multiple locations */}
          {results && (
            <ScenarioCostAnalysisSection
              scenario={scenario}
              currentLocation={location}
              enabled={!!results}
              options={scenarioCostAnalysisOptions}
              costAssumptions={policyCostAssumptions}
              iceMpg={policyCostIceMpg}
              setIceMpg={setPolicyCostIceMpg}
              evKwhPerMile={policyCostEvKwhPerMile}
              setEvKwhPerMile={setPolicyCostEvKwhPerMile}
            />
          )}

          {/* Cost assumptions: last section — drives portfolio, decision summary, and scenario comparison */}
          {results && (
            <PolicyToolCostAssumptionsSection
              assumptions={policyCostAssumptions}
              setAssumptions={setPolicyCostAssumptions}
              iceMpg={policyCostIceMpg}
              setIceMpg={setPolicyCostIceMpg}
              evKwhPerMile={policyCostEvKwhPerMile}
              setEvKwhPerMile={setPolicyCostEvKwhPerMile}
              electricityPriceContext={electricityPriceContext}
              onResetCostAssumptions={resetPolicyCostAssumptions}
            />
          )}
          </>
          )}
        </section>
      </div>
    </div>
  );
};

export default PolicyImpactTool;
